import React, { useState, useEffect, useCallback, lazy, Suspense, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useTranslation } from '../i18n/useTranslation';
import { HeaderBar } from '../components/ui/HeaderBar';
import { LoadingCar } from '../components/LoadingCar';
import { requestOTP, API_BASE_URL as API_URL } from '../services/api';
import { QuickFilters } from '../components/SearchFilters';
import { isWithinRadius } from '../utils/distance';
import { useNotifications } from '../hooks/useNotifications';
import NotificationBanner from '../components/NotificationBanner';
import './EventDashboard.css';

// Lazy load heavy components for faster initial render
const MapLocationPicker = lazy(() => import('../components/MapLocationPicker'));
const RouteMap = lazy(() => import('../components/RouteMap'));
const RoutePicker = lazy(() => import('../components/RoutePicker'));
const RouteWithDetour = lazy(() => import('../components/RouteWithDetour'));
const BusRouteVisualizer = lazy(() => import('../components/BusRouteVisualizer'));

// Simple loading placeholder
const LoadingPlaceholder = () => (
  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
);

// localStorage keys
const STORAGE_KEYS = {
  USER_PROFILE: 'carpool_user_profile',
  MY_OFFERS: 'carpool_my_offers',
  MY_REQUESTS: 'carpool_my_requests',
};

function EventDashboard() {
  const { eventCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, authData, isAuthenticated } = useApp();
  const { t } = useTranslation();
  
  const isDemonstrations = location.pathname.startsWith('/dems');
  
  const [event, setEvent] = useState(null);
  const [offers, setOffers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [myJoinedRides, setMyJoinedRides] = useState([]);
  const [myPendingRequests, setMyPendingRequests] = useState([]); // User's pending ride requests for this event
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('offers');
  const [showModal, setShowModal] = useState(null);
  const [accessCode, setAccessCode] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [joinRequests, setJoinRequests] = useState([]);
  const [rememberMe, setRememberMe] = useState(true);
  const [joinPickupLocation, setJoinPickupLocation] = useState(null);
  const [joinDropoffLocation, setJoinDropoffLocation] = useState(null);
  const [useCustomPickup, setUseCustomPickup] = useState(false);
  const [joinMessage, setJoinMessage] = useState('');
  const [showPassengersModal, setShowPassengersModal] = useState(null);
  const [cancelMessage, setCancelMessage] = useState('');
  const [inviteRequest, setInviteRequest] = useState(null);
  const [inviteOfferId, setInviteOfferId] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  
  // Contact picker modal for offers with multiple organizers
  const [contactPickerOffer, setContactPickerOffer] = useState(null);
  
  // Collapsible sections
  const [confirmedRidesExpanded, setConfirmedRidesExpanded] = useState(false);
  const [contactPickerAction, setContactPickerAction] = useState(null); // 'whatsapp' or 'phone'

  const [showShareMenu, setShowShareMenu] = useState(false);

  // Handle new request notification (in-app toast)
  const handleNewRequest = useCallback((req, message) => {
    showToast(`🙋 ${message}`, 'info');
  }, [showToast]);

  // Notifications hook for live updates
  const {
    isSupported: notificationsSupported,
    permission: notificationPermission,
    requestPermission: requestNotificationPermission,
    startPolling: startNotificationPolling,
    stopPolling: stopNotificationPolling,
    newRequestsCount,
    clearNewRequestsCount
  } = useNotifications(authData, eventCode, handleNewRequest);

  // Track if user has offers in this event
  const [userHasOffers, setUserHasOffers] = useState(false);

  // Event creation fields - label keys for theme, vibe, audience (same as CreateEvent)
  const eventThemeIcons = {
    concert_festival: '🎵',
    demonstration: '🪧',
    party_birthday: '🎉',
    conference_workshop: '🏫',
    sports: '⚽',
    wedding: '👰',
    other: '📌',
  };
  // Floating decorations per event theme (emoji + position % and animation delay)
  const eventThemeFloaters = {
    party_birthday: [
      { emoji: '🎈', left: 8, top: 15, delay: 0, size: 1.2 },
      { emoji: '🎂', left: 88, top: 20, delay: 0.5, size: 1.4 },
      { emoji: '🎈', left: 75, top: 60, delay: 1, size: 1 },
      { emoji: '🎉', left: 15, top: 70, delay: 0.3, size: 1.1 },
      { emoji: '🧁', left: 92, top: 75, delay: 0.8, size: 1 },
      { emoji: '🎈', left: 45, top: 25, delay: 0.6, size: 0.9 },
      { emoji: '🎂', left: 25, top: 55, delay: 0.2, size: 1.1 },
      { emoji: '🎉', left: 65, top: 35, delay: 0.9, size: 1 },
    ],
    wedding: [
      { emoji: '💒', left: 10, top: 18, delay: 0, size: 1.3 },
      { emoji: '💐', left: 85, top: 25, delay: 0.4, size: 1.2 },
      { emoji: '💍', left: 20, top: 65, delay: 0.7, size: 1 },
      { emoji: '🥂', left: 78, top: 60, delay: 0.2, size: 1.1 },
      { emoji: '👰', left: 50, top: 30, delay: 0.5, size: 1.2 },
      { emoji: '💐', left: 5, top: 50, delay: 0.3, size: 1 },
      { emoji: '💒', left: 90, top: 72, delay: 0.8, size: 1.1 },
      { emoji: '🥂', left: 35, top: 22, delay: 0.6, size: 1 },
    ],
    concert_festival: [
      { emoji: '🎵', left: 12, top: 20, delay: 0, size: 1.2 },
      { emoji: '🎸', left: 82, top: 35, delay: 0.5, size: 1.3 },
      { emoji: '🎤', left: 25, top: 65, delay: 0.3, size: 1.1 },
      { emoji: '🎶', left: 70, top: 55, delay: 0.7, size: 1 },
      { emoji: '🎧', left: 88, top: 18, delay: 0.2, size: 1.1 },
      { emoji: '🎵', left: 50, top: 75, delay: 0.6, size: 1 },
      { emoji: '🎸', left: 8, top: 45, delay: 0.4, size: 1 },
      { emoji: '🎤', left: 92, top: 62, delay: 0.9, size: 1.1 },
    ],
    sports: [
      { emoji: '⚽', left: 15, top: 22, delay: 0, size: 1.2 },
      { emoji: '🏀', left: 80, top: 30, delay: 0.4, size: 1.1 },
      { emoji: '🏆', left: 50, top: 18, delay: 0.6, size: 1.3 },
      { emoji: '⚽', left: 25, top: 68, delay: 0.2, size: 1 },
      { emoji: '🎾', left: 88, top: 58, delay: 0.7, size: 1.1 },
      { emoji: '🏆', left: 8, top: 52, delay: 0.5, size: 1 },
      { emoji: '🏀', left: 72, top: 72, delay: 0.3, size: 1.1 },
      { emoji: '🎾', left: 40, top: 38, delay: 0.8, size: 1 },
    ],
    conference_workshop: [
      { emoji: '📚', left: 10, top: 25, delay: 0, size: 1.1 },
      { emoji: '💡', left: 85, top: 35, delay: 0.5, size: 1.2 },
      { emoji: '📋', left: 22, top: 62, delay: 0.3, size: 1 },
      { emoji: '🏫', left: 75, top: 55, delay: 0.6, size: 1.2 },
      { emoji: '📚', left: 48, top: 15, delay: 0.2, size: 1 },
      { emoji: '💡', left: 90, top: 70, delay: 0.7, size: 1.1 },
      { emoji: '📋', left: 5, top: 48, delay: 0.4, size: 1 },
      { emoji: '🏫', left: 60, top: 28, delay: 0.8, size: 1.1 },
    ],
    demonstration: [
      { emoji: '🪧', left: 12, top: 28, delay: 0, size: 1.1 },
      { emoji: '✊', left: 84, top: 40, delay: 0.4, size: 1 },
      { emoji: '🪧', left: 28, top: 65, delay: 0.6, size: 1 },
      { emoji: '📢', left: 70, top: 22, delay: 0.2, size: 1.1 },
      { emoji: '✊', left: 50, top: 50, delay: 0.5, size: 1 },
      { emoji: '📢', left: 8, top: 55, delay: 0.3, size: 1 },
      { emoji: '🪧', left: 88, top: 68, delay: 0.7, size: 1.1 },
    ],
    other: [
      { emoji: '✨', left: 20, top: 30, delay: 0, size: 1 },
      { emoji: '✨', left: 80, top: 45, delay: 0.4, size: 1 },
      { emoji: '✨', left: 45, top: 65, delay: 0.6, size: 1 },
      { emoji: '✨', left: 10, top: 60, delay: 0.2, size: 1 },
      { emoji: '✨', left: 90, top: 25, delay: 0.5, size: 1 },
    ],
  };
  const eventThemeLabelKeys = {
    concert_festival: 'eventTypeConcert',
    demonstration: 'eventTypeDemonstration',
    party_birthday: 'eventTypeParty',
    conference_workshop: 'eventTypeConference',
    sports: 'eventTypeSports',
    wedding: 'eventTypeWedding',
    other: 'eventTypeOther',
  };
  const eventVibeLabelKeys = {
    fun_social: 'eventVibeFun',
    chill: 'eventVibeChill',
    high_energy: 'eventVibeHighEnergy',
    calm: 'eventVibeCalm',
    purpose_driven: 'eventVibePurpose',
  };
  const audienceLabelKeys = { small: 'audienceSmall', medium: 'audienceMedium', large: 'audienceLarge' };

  const getEventUrl = () => {
    const baseUrl = window.location.origin;
    const path = isDemonstrations ? `/dems/event/${eventCode}` : `/event/${eventCode}`;
    return `${baseUrl}${path}`;
  };

  const copyEventLink = async () => {
    try {
      await navigator.clipboard.writeText(getEventUrl());
      showToast(t('eventDashboard.linkCopied') || 'تم نسخ الرابط!', 'success');
      setShowShareMenu(false);
    } catch {
      showToast('Failed to copy', 'error');
    }
  };

  const shareViaWhatsApp = () => {
    const url = getEventUrl();
    const text = `${event?.event_name || 'Event'}\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    setShowShareMenu(false);
  };

  const shareViaTelegram = () => {
    const url = getEventUrl();
    const text = event?.event_name || 'Event';
    window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
    setShowShareMenu(false);
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.event_name || 'Event',
          text: `انضم للحدث: ${event?.event_name}`,
          url: getEventUrl()
        });
        setShowShareMenu(false);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
  };

  // When returning from Login after OTP: complete publish with stored form data
  const publishAfterLoginRef = React.useRef(false);
  useEffect(() => {
    const state = location.state || {};
    if (!state.publishFormData || !state.publishType || !authData || publishAfterLoginRef.current) return;
    publishAfterLoginRef.current = true;
    const token = localStorage.getItem('authToken');
    executePublish(state.publishType, token, state.publishFormData).finally(() => {
      publishAfterLoginRef.current = false;
      navigate(location.pathname + location.search, { replace: true, state: {} });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when auth/state from login; executePublish is stable enough
  }, [authData, location.state, location.pathname, location.search, navigate]);

  // Track user's own posts
  const [myOfferIds, setMyOfferIds] = useState([]);
  const [myRequestIds, setMyRequestIds] = useState([]);

  // Filter and sort state
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('newest');

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    seats: '3',
    description: '',
    tripType: 'both',
    pickupLocation: '',
    pickupLat: null,
    pickupLng: null,
    sameReturnLocation: true,
    returnLocation: '',
    returnLat: null,
    returnLng: null,
    preference: 'any', // 'any', 'male', 'female'
    gender: '', // 'male', 'female' - user's own gender
    hideName: false, // Hide name from other users
    hidePhone: false, // Hide phone from other users
    hideEmail: false, // Hide email from other users
    paymentRequired: 'not_required', // 'obligatory', 'optional', 'not_required'
    paymentAmount: '', // Price in currency
    paymentMethod: '', // 'bit', 'paybox', 'cash'
    passengerCount: 1, // Number of passengers requesting to join
  });
  
  // Route info for calculating payment
  const [routeDistance, setRouteDistance] = useState(null);
  const [maxPayment, setMaxPayment] = useState(0);
  
  // Pending publish data for after OTP verification

  // Load user profile from authData (logged in user) or localStorage
  useEffect(() => {
    // Priority: authData (logged in) > localStorage
    if (isAuthenticated && authData) {
      setFormData(prev => ({
        ...prev,
        name: authData.name || prev.name,
        phone: authData.phone || prev.phone,
        email: authData.email || prev.email,
        gender: authData.gender || prev.gender,
      }));
    }

    const savedOffers = localStorage.getItem(STORAGE_KEYS.MY_OFFERS);
    if (savedOffers) {
      setMyOfferIds(JSON.parse(savedOffers));
    }

    const savedRequests = localStorage.getItem(STORAGE_KEYS.MY_REQUESTS);
    if (savedRequests) {
      setMyRequestIds(JSON.parse(savedRequests));
    }
  }, [isAuthenticated, authData]);

  // Save user profile to localStorage
  const saveUserProfile = (data) => {
    if (rememberMe) {
      localStorage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify({
        name: data.name,
        phone: data.phone,
        email: data.email,
      }));
    }
  };

  // Track my offer
  const addMyOffer = (offerId) => {
    const updated = [...myOfferIds, offerId];
    setMyOfferIds(updated);
    localStorage.setItem(STORAGE_KEYS.MY_OFFERS, JSON.stringify(updated));
  };

  // Track my request
  const addMyRequest = (requestId) => {
    const updated = [...myRequestIds, requestId];
    setMyRequestIds(updated);
    localStorage.setItem(STORAGE_KEYS.MY_REQUESTS, JSON.stringify(updated));
  };

  // Remove my offer
  const removeMyOffer = (offerId) => {
    const updated = myOfferIds.filter(id => id !== offerId);
    setMyOfferIds(updated);
    localStorage.setItem(STORAGE_KEYS.MY_OFFERS, JSON.stringify(updated));
  };

  // Remove my request
  const removeMyRequest = (requestId) => {
    const updated = myRequestIds.filter(id => id !== requestId);
    setMyRequestIds(updated);
    localStorage.setItem(STORAGE_KEYS.MY_REQUESTS, JSON.stringify(updated));
  };

  const fetchEvent = useCallback(async (code = null) => {
    try {
      const url = code 
        ? `${API_URL}/event/${eventCode}?access_code=${encodeURIComponent(code)}`
        : `${API_URL}/event/${eventCode}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (!response.ok) {
        setError('Event not found');
        setLoading(false);
        return null;
      }
      
      if (data.needs_code) {
        setNeedsCode(true);
        setEvent({ event_name: data.event_name, event_id: data.event_id });
        setLoading(false);
        return null;
      }
      
      setEvent(data);
      setNeedsCode(false);
      console.log('Event data received:', { 
        event_name: data.event_name, 
        event_image: data.event_image,
        has_image: !!data.event_image,
        event_theme: data.event_theme,
        event_vibe: data.event_vibe,
        audience_size: data.audience_size,
        event_short_description: data.event_short_description,
        event_notes: data.event_notes,
        is_featured: data.is_featured 
      });
      
      // Save access code to sessionStorage if code was provided and validation succeeded
      if (code && eventCode) {
        sessionStorage.setItem(`event_access_code_${eventCode}`, code);
      }
      
      return data;
    } catch (err) {
      setError('Failed to load event');
      setLoading(false);
      return null;
    }
  }, [eventCode]);

  const fetchOffers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/event/${eventCode}/offers`);
      const data = await response.json();
      console.log('Fetched offers:', data.offers);
      // Debug: Check passenger_count in confirmed_passengers
      data.offers?.forEach(offer => {
        if (offer.confirmed_passengers?.length > 0) {
          console.log(`Offer ${offer.offer_id} confirmed_passengers:`, offer.confirmed_passengers.map(p => ({ name: p.name, passenger_count: p.passenger_count })));
        }
      });
      setOffers(data.offers || []);
    } catch (err) {
      console.error('Error fetching offers:', err);
    }
  }, [eventCode]);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/event/${eventCode}/requests`);
      const data = await response.json();
      setRequests(data.requests || []);
    } catch (err) {
    }
  }, [eventCode]);

  // Fetch rides where user is a passenger (confirmed join requests for this event)
  const fetchMyJoinedRides = useCallback(async () => {
    if (!isAuthenticated || !event?.event_id) {
      setMyJoinedRides([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setMyJoinedRides([]);
        return;
      }
      
      const response = await fetch(`${API_URL}/auth/my-joined-rides`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter to only show confirmed rides for this event and map to expected structure
        const eventRides = (data.rides || [])
          .filter(ride => ride.event_id === event.event_id && ride.status === 'confirmed')
          .map(ride => ({
            join_request_id: ride.join_request_id,
            offer_id: ride.offer_id,
            status: ride.status,
            driverName: ride.driver_name,
            driverPhone: ride.driver_phone,
            driverEmail: ride.driver_email,
            pickupLocation: ride.pickup_location,
            pickupLat: ride.pickup_lat,
            pickupLng: ride.pickup_lng,
            message: ride.message || ride.notes,
            eventName: ride.event_name,
            eventLocation: ride.event_location,
            offerLocations: ride.offer_locations || (ride.offer_location ? [{ location_address: ride.offer_location }] : []),
            totalSeats: ride.total_seats,
            availableSeats: ride.available_seats
          }));
        
        // Deduplicate by join_request_id
        const uniqueRides = eventRides.filter((ride, index, self) =>
          index === self.findIndex(r => r.join_request_id === ride.join_request_id)
        );
        setMyJoinedRides(uniqueRides);
      } else {
        setMyJoinedRides([]);
      }
    } catch (err) {
      setMyJoinedRides([]);
    }
  }, [isAuthenticated, event?.event_id]);

  // Fetch user's pending ride requests for this event (for matching suggestions)
  const fetchMyPendingRequests = useCallback(async () => {
    if (!isAuthenticated || !event?.event_id) {
      setMyPendingRequests([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setMyPendingRequests([]);
        return;
      }
      
      const response = await fetch(`${API_URL}/auth/my-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📋 Raw API response from /auth/my-requests:', data);
        console.log('📋 Current event_id:', event.event_id);
        
        // The API might return data.requests or just data as an array
        const allRequests = data.requests || data || [];
        console.log('📋 All requests from API:', allRequests);
        console.log('📋 All requests details:', allRequests.map(r => ({
          request_id: r.request_id || r.id,
          event_id: r.event_id,
          status: r.status,
          pickup_location: r.pickup_location,
          pickup_lat: r.pickup_lat,
          pickup_lng: r.pickup_lng
        })));
        
        // Filter to only show pending/active requests for this event (active = still looking for a ride)
        const eventRequests = allRequests
          .filter(req => {
            const reqEventId = req.event_id;
            const reqStatus = req.status;
            // Include both 'pending' and 'active' statuses (active means still looking for a ride)
            const matches = reqEventId === event.event_id && (reqStatus === 'pending' || reqStatus === 'active');
            console.log(`📋 Request ${req.request_id || req.id}: event_id=${reqEventId} (matches ${event.event_id}? ${reqEventId === event.event_id}), status=${reqStatus} (pending/active? ${reqStatus === 'pending' || reqStatus === 'active'}), overall match=${matches}`);
            return matches;
          })
          .map(req => {
            // Get coordinates from locations array (prioritize "going" direction)
            const goingLocation = req.locations?.find(loc => loc.trip_direction === 'going');
            const firstLocation = req.locations?.[0];
            const location = goingLocation || firstLocation;
            
            return {
              request_id: req.request_id || req.id,
              pickup_location: req.pickup_location || location?.location_address,
              pickup_lat: req.pickup_lat || location?.location_lat,
              pickup_lng: req.pickup_lng || location?.location_lng,
              trip_type: req.trip_type,
              locations: req.locations || []
            };
          });
        console.log('📋 Filtered pending requests for this event:', eventRequests);
        setMyPendingRequests(eventRequests);
      } else {
        console.log('❌ Failed to fetch pending requests:', response.status, await response.text());
        setMyPendingRequests([]);
      }
    } catch (err) {
      setMyPendingRequests([]);
    }
  }, [isAuthenticated, event?.event_id]);

  const fetchJoinRequests = async (offerId) => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_URL}/carpool/offer/${offerId}/join-requests`, {
        headers
      });
      const data = await response.json();
      console.log('Join requests data:', data); // Debug log
      console.log('Join requests with passenger_count:', data.requests?.map(r => ({ name: r.name, passenger_count: r.passenger_count })));
      setJoinRequests(data.requests || []);
    } catch (err) {
      console.error('Error fetching join requests:', err);
      setJoinRequests([]);
    }
  };

  // Check for stored access code on mount and load event
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // Check for stored access code
      if (eventCode) {
        const storedCode = sessionStorage.getItem(`event_access_code_${eventCode}`);
        if (storedCode) {
          setAccessCode(storedCode);
          // Auto-validate with stored code
          const eventData = await fetchEvent(storedCode);
          if (eventData) {
            await Promise.all([fetchOffers(), fetchRequests()]);
          }
          setLoading(false);
          return;
        }
      }
      
      // No stored code, load normally
      const eventData = await fetchEvent();
      if (eventData) {
        await Promise.all([fetchOffers(), fetchRequests()]);
      }
      setLoading(false);
    };
    loadData();
  }, [eventCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch my joined rides and pending requests when event and auth status are available
  useEffect(() => {
    if (event?.event_id && isAuthenticated !== undefined) {
      fetchMyJoinedRides();
      fetchMyPendingRequests();
    }
  }, [event?.event_id, isAuthenticated, fetchMyJoinedRides, fetchMyPendingRequests]);

  // Refresh pending requests when switching to offers tab (to highlight matching offers)
  useEffect(() => {
    if (activeTab === 'offers' && isAuthenticated && event?.event_id) {
      fetchMyPendingRequests();
    }
  }, [activeTab, isAuthenticated, event?.event_id, fetchMyPendingRequests]);

  // Check if user has offers in this event and start polling for new requests
  useEffect(() => {
    if (!isAuthenticated || !authData || offers.length === 0) {
      setUserHasOffers(false);
      return;
    }

    // Check if any offer belongs to the current user
    const myOffers = offers.filter(offer => 
      offer.driver_phone === authData.phone || 
      offer.owner_account_id === authData.account_id
    );

    const hasOffers = myOffers.length > 0;
    setUserHasOffers(hasOffers);
    
    console.log('🔔 User has offers:', hasOffers, 'myOffers:', myOffers.length);

    // Start polling if user has offers (for in-app toasts, even without push permission)
    if (hasOffers) {
      console.log('🔔 Starting notification polling...');
      startNotificationPolling(30000); // Check every 30 seconds
    }

    // Cleanup: stop polling when component unmounts or user leaves
    return () => {
      stopNotificationPolling();
    };
  }, [offers, isAuthenticated, authData, startNotificationPolling, stopNotificationPolling]);

  // Clear new requests count when viewing join requests modal
  useEffect(() => {
    if (showModal === 'join-requests' && newRequestsCount > 0) {
      clearNewRequestsCount();
    }
  }, [showModal, newRequestsCount, clearNewRequestsCount]);

  // Refresh offers when new requests are detected
  useEffect(() => {
    if (newRequestsCount > 0) {
      // Refresh offers to update pending request counts in UI
      fetchOffers();
    }
  }, [newRequestsCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-register user as event participant when they access an event
  useEffect(() => {
    const registerEventParticipation = async () => {
      if (!isAuthenticated || !event?.event_id) return;
      
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        
        await fetch(`${API_URL}/event/${event.event_id}/join`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (err) {
      }
    };
    
    registerEventParticipation();
  }, [isAuthenticated, event?.event_id]);

  // Auto-copy pickup to return
  useEffect(() => {
    if (formData.sameReturnLocation) {
      setFormData(prev => ({ 
        ...prev, 
        returnLocation: prev.pickupLocation,
        returnLat: prev.pickupLat,
        returnLng: prev.pickupLng
      }));
    }
  }, [formData.pickupLocation, formData.pickupLat, formData.pickupLng, formData.sameReturnLocation]);

  // Clean up pac-containers when modal closes
  useEffect(() => {
    if (!showModal) {
      // Hide any open Google Places dropdowns
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        try {
          container.style.display = 'none';
        } catch (e) {}
      });
    }
  }, [showModal]);

  const handleAccessCodeSubmit = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) {
      setError('Please enter the access code');
      return;
    }
    setLoading(true);
    setError('');
    const code = accessCode.trim();
    const eventData = await fetchEvent(code);
    if (eventData) {
      // Save access code to sessionStorage for this event
      sessionStorage.setItem(`event_access_code_${eventCode}`, code);
      await Promise.all([fetchOffers(), fetchRequests()]);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle route calculation result
  const handleRouteCalculated = useCallback((routeInfo) => {
    if (routeInfo && routeInfo.distance) {
      // Extract numeric value from distance string (e.g., "15.2 ק"מ" -> 15.2)
      const distanceMatch = routeInfo.distance.match(/[\d.]+/);
      if (distanceMatch) {
        const distanceKm = parseFloat(distanceMatch[0]);
        setRouteDistance(distanceKm);
        const calculatedMax = Math.round(0.5 * distanceKm * 100) / 100;
        setMaxPayment(calculatedMax);
        
        // Auto-fill payment amount if payment is required
        setFormData(prev => {
          if (prev.paymentRequired !== 'not_required' && (!prev.paymentAmount || prev.paymentAmount === '')) {
            return { ...prev, paymentAmount: calculatedMax.toFixed(2) };
          }
          return prev;
        });
      }
    }
  }, []);

  const resetForm = () => {
    // Use authData for logged-in users, empty for non-logged in
    const userData = isAuthenticated && authData ? authData : {};
    
    setFormData({
      name: userData.name || '',
      phone: userData.phone || '',
      email: userData.email || '',
      seats: '3',
      description: '',
      tripType: 'both',
      pickupLocation: '',
      pickupLat: null,
      pickupLng: null,
      sameReturnLocation: true,
      returnLocation: '',
      returnLat: null,
      returnLng: null,
      preference: 'any',
      gender: userData.gender || '',
      hideName: false,
      hidePhone: false,
      hideEmail: false,
      paymentRequired: 'not_required',
      paymentAmount: '',
      paymentMethod: '',
      passengerCount: 1,
    });
    setEditingItem(null);
    setRouteDistance(null);
    setMaxPayment(0);
    
    // Clean up Google Places autocomplete dropdowns
    setTimeout(() => {
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        try {
          container.style.display = 'none';
        } catch (e) {}
      });
    }, 100);
  };

  // Validate form data before publishing
  const validateForm = (type) => {
    if (!formData.name || !formData.phone || !formData.email || !formData.pickupLocation) {
      showToast('Please fill in all required fields', 'error');
      return false;
    }

    if (!formData.gender) {
      showToast('Please select your gender', 'error');
      return false;
    }

    // Validate payment amount and method if payment is required
    if (type === 'offer' && (formData.paymentRequired === 'optional' || formData.paymentRequired === 'obligatory')) {
      if (!formData.paymentAmount || parseFloat(formData.paymentAmount) <= 0) {
        showToast('Please enter a valid payment amount', 'error');
        return false;
      }
      
      if (!formData.paymentMethod) {
        showToast('Please select a payment method', 'error');
        return false;
      }
      
      // Validate max payment using stored maxPayment or calculate
      const currentMax = maxPayment > 0 ? maxPayment : (() => {
        if (formData.pickupLat && formData.pickupLng && event?.event_lat && event?.event_lng) {
          const R = 6371;
          const dLat = (event.event_lat - formData.pickupLat) * Math.PI / 180;
          const dLon = (event.event_lng - formData.pickupLng) * Math.PI / 180;
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                    Math.cos(formData.pickupLat * Math.PI / 180) * Math.cos(event.event_lat * Math.PI / 180) *
                    Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          return 0.5 * R * c;
        }
        return 0;
      })();
      
      if (currentMax > 0 && parseFloat(formData.paymentAmount) > currentMax) {
        showToast(`Payment amount cannot exceed ₪${currentMax.toFixed(2)}`, 'error');
        return false;
      }
    }
    return true;
  };

  // Execute the actual publish after validation/auth. dataOverride used when returning from login.
  const executePublish = async (type, authToken = null, dataOverride = null) => {
    const data = dataOverride || formData;
    setSubmitting(true);
    saveUserProfile(data);

    const locations = [];
    const returnLoc = data.sameReturnLocation ? data.pickupLocation : data.returnLocation;

    if (data.tripType === 'going' || data.tripType === 'both') {
      locations.push({
        location_address: data.pickupLocation,
        lat: data.pickupLat,
        lng: data.pickupLng,
        trip_direction: 'going',
        time_type: 'flexible',
        specific_time: null,
      });
    }

    if (data.tripType === 'return' || data.tripType === 'both') {
      locations.push({
        location_address: returnLoc,
        lat: data.sameReturnLocation ? data.pickupLat : data.returnLat,
        lng: data.sameReturnLocation ? data.pickupLng : data.returnLng,
        trip_direction: 'return',
        time_type: 'flexible',
        specific_time: null,
      });
    }

    try {
      const isEditing = editingItem !== null;
      const endpoint = type === 'offer'
        ? (isEditing ? `${API_URL}/carpool/offer/${editingItem.offer_id}` : `${API_URL}/carpool/offer`)
        : (isEditing ? `${API_URL}/carpool/request/${editingItem.request_id}` : `${API_URL}/carpool/request`);

      const headers = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const body = type === 'offer' ? {
        event_id: event.event_id,
        name: data.name,
        phone: data.phone,
        email: data.email,
        gender: data.gender,
        hide_name: data.hideName,
        hide_phone: data.hidePhone,
        hide_email: data.hideEmail,
        total_seats: parseInt(data.seats),
        description: data.description,
        trip_type: data.tripType,
        privacy: 'public',
        preference: data.preference,
        payment_required: data.paymentRequired,
        payment_amount: data.paymentRequired !== 'not_required' ? parseFloat(data.paymentAmount) : null,
        payment_method: data.paymentRequired !== 'not_required' ? data.paymentMethod : null,
        locations,
      } : {
        event_id: event.event_id,
        name: data.name,
        phone: data.phone,
        email: data.email,
        gender: data.gender,
        hide_name: data.hideName,
        hide_phone: data.hidePhone,
        hide_email: data.hideEmail,
        trip_type: data.tripType,
        preference: data.preference,
        passenger_count: data.passengerCount || 1,
        locations,
      };

      const response = await fetch(endpoint, {
        method: isEditing ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const responseData = await response.json();

      if (response.ok) {
        if (!isEditing) {
          if (type === 'offer') {
            addMyOffer(responseData.offer_id);
          } else {
            addMyRequest(responseData.request_id);
          }
        }
        showToast(isEditing ? (t('eventDashboard.updatedSuccessfully') || 'تم التحديث بنجاح!') : (type === 'offer' ? t('publishRide.ridePublished') : t('publishRide.requestSent')), 'success');
        setShowModal(null);
        resetForm();
        await Promise.all([fetchOffers(), fetchRequests(), fetchMyPendingRequests()]);
      } else {
        showToast(responseData.message || 'Failed to publish', 'error');
      }
    } catch (err) {
      const errorMessage = err.message?.includes('timed out')
        ? 'Request timed out. Please check your connection and try again.'
        : 'Error publishing. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async (type) => {
    if (!validateForm(type)) return;

    // If user is authenticated, publish directly
    if (isAuthenticated) {
      const token = localStorage.getItem('authToken');
      await executePublish(type, token);
      return;
    }

    // Not logged in: send OTP then redirect to Login; after verify, return here and publish
    setSubmitting(true);
    try {
      const result = await requestOTP(formData.phone, formData.name, formData.email, formData.gender);

      if (result.requires_registration) {
        showToast(t('eventDashboard.fillDetails') || 'Please fill in your details', 'info');
        setSubmitting(false);
        return;
      }

      if (result.success && result.requires_otp) {
        navigate('/login', {
          state: {
            returnTo: location.pathname + location.search,
            publishFormData: formData,
            publishType: type,
          },
        });
        setSubmitting(false);
        return;
      }

      showToast(result.message || 'Something went wrong', 'error');
    } catch (err) {
      const errorMessage = err.message?.includes('timed out')
        ? 'Request timed out. Please try again.'
        : 'Failed to send code. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (type, item) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) {
      return;
    }

    try {
      const endpoint = type === 'offer' 
        ? `${API_URL}/carpool/offer/${item.offer_id}`
        : `${API_URL}/carpool/request/${item.request_id}`;

      const response = await fetch(endpoint, { method: 'DELETE' });

      if (response.ok) {
        if (type === 'offer') {
          removeMyOffer(item.offer_id);
        } else {
          removeMyRequest(item.request_id);
        }
        showToast('Deleted successfully!', 'success');
        await Promise.all([fetchOffers(), fetchRequests()]);
      } else {
        showToast('Failed to delete', 'error');
      }
    } catch (err) {
      showToast('Error deleting. Please try again.', 'error');
    }
  };

  const handleEdit = (type, item) => {
    // Store edit data in localStorage and navigate to PublishRidePage
    const editData = {
      type,
      item,
      editId: type === 'offer' ? item.offer_id : item.request_id
    };
    localStorage.setItem('editRideData', JSON.stringify(editData));
    
    // Navigate to publish page with edit mode
    const basePath = isDemonstrations ? `/dems/event/${eventCode}/publish` : `/event/${eventCode}/publish`;
    navigate(`${basePath}?mode=${type}&edit=true`);
  };

  // Open join modal for a specific offer
  const handleOpenJoinModal = (offer) => {
    const confirmed = offer.confirmed_passengers?.reduce((sum, p) => sum + (parseInt(p.passenger_count) || 1), 0) || 0;
    const total = offer.total_seats || 0;
    const available = total - confirmed;
    const isFull = available <= 0 || (offer.available_seats !== undefined && Number(offer.available_seats) <= 0);
    if (isFull) {
      showToast(t('eventDashboard.full') || 'Offer is full', 'error');
      return;
    }

    setSelectedOffer(offer);
    setJoinPickupLocation(null); // Reset pickup location
    // Pre-fill with authData for logged-in users
    if (isAuthenticated && authData) {
      setFormData(prev => ({
        ...prev,
        name: authData.name || '',
        phone: authData.phone || '',
        email: authData.email || '',
        gender: authData.gender || prev.gender,
      }));
    }
    setShowModal('join-ride');
  };

  const handleOpenInviteModal = (request) => {
    setInviteRequest(request);
    setInviteOfferId(myOffers[0]?.offer_id || '');
    setShowModal('invite-request');
  };

  const handleSendInvitation = async () => {
    if (!inviteRequest?.request_id) {
      showToast('Request not found', 'error');
      return;
    }
    if (!inviteOfferId) {
      showToast('Select an offer to send', 'error');
      return;
    }
    setInviteSending(true);
    try {
      const response = await fetch(`${API_URL}/carpool/offer/${inviteOfferId}/send-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: inviteRequest.request_id })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to send invitation');
      }
      showToast('Invitation sent!', 'success');
      setShowModal(null);
      setInviteRequest(null);
      setInviteOfferId('');
    } catch (err) {
      showToast(err.message || 'Failed to send invitation', 'error');
    } finally {
      setInviteSending(false);
    }
  };

  // Submit join request
  const handleSubmitJoinRequest = async () => {
    
    if (!formData.name || !formData.phone || !formData.email) {
      showToast('Please fill in your contact details', 'error');
      return;
    }

    // More detailed validation with logging
    if (!joinPickupLocation) {
      showToast('Please select a pickup location on the route', 'error');
      return;
    }
    
    if (joinPickupLocation.lat == null || joinPickupLocation.lng == null) {
      showToast('Please select a valid pickup location with coordinates', 'error');
      return;
    }

    setSubmitting(true);
    saveUserProfile(formData);

    try {
      const requestBody = {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
        pickup_location: joinPickupLocation.address || '',
        pickup_lat: Number(joinPickupLocation.lat),
        pickup_lng: Number(joinPickupLocation.lng),
        passenger_count: formData.passengerCount || 1,
        // Note: _pickupIndex and _dropoffIndex are internal tracking fields, not sent to API
        message: joinMessage || '',
      };
      
      
      const response = await fetch(`${API_URL}/carpool/offer/${selectedOffer.offer_id}/request-join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (response.ok) {
        showToast('Request sent! The driver will be notified.', 'success');
        setShowModal(null);
        setJoinPickupLocation(null);
        setJoinMessage('');
        await fetchOffers();
        // Refresh my joined rides after sending request
        if (isAuthenticated) {
          await fetchMyJoinedRides();
        }
      } else {
        showToast(responseData.message || 'Failed to send request', 'error');
      }
    } catch (err) {
      const errorMessage = err.message?.includes('timed out')
        ? 'Request timed out. Please check your connection and try again.'
        : 'Error sending request. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewJoinRequests = async (offer) => {
    setSelectedOffer(offer);
    await fetchJoinRequests(offer.offer_id);
    setShowModal('join-requests');
  };

  const handleAcceptJoinRequest = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/carpool/offer/${selectedOffer.offer_id}/accept-join/${requestId}`, {
        method: 'POST',
      });

      if (response.ok) {
        showToast('Passenger confirmed!', 'success');
        await fetchJoinRequests(selectedOffer.offer_id);
        await fetchOffers();
        // Refresh my joined rides in case this was my request
        if (isAuthenticated) {
          await fetchMyJoinedRides();
        }
      } else {
        showToast('Failed to accept request', 'error');
      }
    } catch (err) {
      showToast('Error accepting request', 'error');
    }
  };

  const handleRejectJoinRequest = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/carpool/offer/${selectedOffer.offer_id}/reject-join/${requestId}`, {
        method: 'POST',
      });

      if (response.ok) {
        showToast('Request declined', 'success');
        await fetchJoinRequests(selectedOffer.offer_id);
      } else {
        showToast('Failed to decline request', 'error');
      }
    } catch (err) {
      showToast('Error declining request', 'error');
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    // Strip all non-digits
    let cleaned = phone.replace(/\D/g, '');
    // Convert Israeli local format (05x) to international (9725x)
    if (cleaned.startsWith('0') && cleaned.length === 10) {
      cleaned = '972' + cleaned.substring(1);
    }
    // If already has 972 prefix, ensure no leading zeros after it
    if (cleaned.startsWith('972') && cleaned.length === 12) {
      return cleaned;
    }
    return cleaned;
  };

  // Get all contacts for an offer (driver + organizers)
  const getOfferContacts = (offer) => {
    const contacts = [];
    // Add main driver/owner
    if (offer.driver_phone || offer.phone) {
      contacts.push({
        name: offer.driver_name || offer.name || t('eventDashboard.driver') || 'السائق',
        phone: offer.driver_phone || offer.phone,
        isMain: true
      });
    }
    // Add organizers if this is a public vehicle
    if (offer.organizers && Array.isArray(offer.organizers)) {
      offer.organizers.forEach(org => {
        if (org.name && org.phone) {
          contacts.push({
            name: org.name,
            phone: org.phone,
            isMain: false
          });
        }
      });
    }
    return contacts;
  };

  // Check if offer has multiple contacts (used internally by handleContactClick)
  // eslint-disable-next-line no-unused-vars
  const hasMultipleContacts = (offer) => {
    return getOfferContacts(offer).length > 1;
  };

  // Handle contact button click
  const handleContactClick = (offer, action) => {
    const contacts = getOfferContacts(offer);
    if (contacts.length > 1) {
      // Show contact picker modal
      setContactPickerOffer(offer);
      setContactPickerAction(action);
    } else if (contacts.length === 1) {
      // Direct contact
      const contact = contacts[0];
      if (action === 'whatsapp') {
        window.open(`https://wa.me/${formatPhone(contact.phone)}?text=Hi! I saw your carpool offer for ${event?.event_name}`, '_blank');
      } else {
        window.location.href = `tel:${contact.phone}`;
      }
    }
  };

  // Handle contact selection from picker
  const handleContactSelect = (contact) => {
    if (contactPickerAction === 'whatsapp') {
      window.open(`https://wa.me/${formatPhone(contact.phone)}?text=Hi! I saw your carpool offer for ${event?.event_name}`, '_blank');
    } else {
      window.location.href = `tel:${contact.phone}`;
    }
    setContactPickerOffer(null);
    setContactPickerAction(null);
  };

  const isMyOffer = (offer) => myOfferIds.includes(offer.offer_id);
  const isMyRequest = (request) => myRequestIds.includes(request.request_id);
  const myOffers = useMemo(
    () => offers.filter(offer => myOfferIds.includes(offer.offer_id)),
    [offers, myOfferIds]
  );

  // Filter and sort offers
  const filteredOffers = useMemo(() => {
    let result = [...offers];

    // Apply filters
    if (filters.tripType) {
      result = result.filter(o => o.trip_type === filters.tripType);
    }
    if (filters.preference && filters.preference !== 'any') {
      result = result.filter(o => o.preference === filters.preference || o.preference === 'any');
    }
    if (filters.payment) {
      if (filters.payment === 'free') {
        result = result.filter(o => o.payment_required === 'not_required');
      } else if (filters.payment === 'optional') {
        result = result.filter(o => o.payment_required === 'optional');
      } else if (filters.payment === 'required') {
        result = result.filter(o => o.payment_required === 'obligatory');
      }
    }
    if (filters.seats) {
      const minSeats = filters.seats === '4+' ? 4 : parseInt(filters.seats);
      result = result.filter(o => {
        const confirmed = o.confirmed_passengers?.reduce((sum, p) => sum + (p.passenger_count || 1), 0) || 0;
        const available = (o.total_seats || 0) - confirmed;
        return available >= minSeats;
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'seats_available':
          const aConfirmed = a.confirmed_passengers?.reduce((sum, p) => sum + (p.passenger_count || 1), 0) || 0;
          const bConfirmed = b.confirmed_passengers?.reduce((sum, p) => sum + (p.passenger_count || 1), 0) || 0;
          const aSeats = (a.total_seats || 0) - aConfirmed;
          const bSeats = (b.total_seats || 0) - bConfirmed;
          return bSeats - aSeats;
        case 'price_low':
          return (parseFloat(a.payment_amount) || 0) - (parseFloat(b.payment_amount) || 0);
        case 'price_high':
          return (parseFloat(b.payment_amount) || 0) - (parseFloat(a.payment_amount) || 0);
        case 'newest':
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });

    return result;
  }, [offers, filters, sortBy]);

  const getSeatsDisplay = (offer) => {
    const total = offer.total_seats || 0;
    // Sum passenger_count from all confirmed passengers, not just count requests
    const confirmed = offer.confirmed_passengers?.reduce((sum, p) => {
      const count = parseInt(p.passenger_count) || 1;
      console.log('Passenger:', p.name, 'passenger_count:', p.passenger_count, 'parsed:', count);
      return sum + count;
    }, 0) || 0;
    const available = total - confirmed;
    console.log('Seats display for offer:', offer.offer_id, 'total:', total, 'confirmed:', confirmed, 'available:', available, 'confirmed_passengers:', offer.confirmed_passengers);
    return { total, confirmed, available };
  };

  const getPreferenceLabel = (pref) => {
    switch (pref) {
      case 'male': return { icon: '♂️', text: 'Males preferred' };
      case 'female': return { icon: '♀️', text: 'Females preferred' };
      default: return null;
    }
  };

  // Check if an offer matches user's pending request location (within 5km radius)
  const isMatchingOffer = useCallback((offer) => {
    console.log('🔍 isMatchingOffer called for offer:', offer.offer_id, 'myPendingRequests:', myPendingRequests);
    if (!myPendingRequests || myPendingRequests.length === 0) {
      console.log('❌ No pending requests found');
      return false;
    }
    
    // Helper function to get lat/lng from location (handles both formats)
    const getLocationCoords = (loc) => {
      const lat = loc.lat || loc.location_lat;
      const lng = loc.lng || loc.location_lng;
      return { lat, lng };
    };
    
    // Get offer locations (for buses, check all pickup locations; for cars, prioritize "going" location)
    const offerLocations = [];
    if (offer.vehicle_type === 'public' && offer.locations) {
      // For buses, get all going-direction pickup locations
      offer.locations
        .filter(loc => {
          const coords = getLocationCoords(loc);
          return (loc.trip_direction === 'going' || loc.location_type === 'pickup') && coords.lat && coords.lng;
        })
        .forEach(loc => {
          const coords = getLocationCoords(loc);
          offerLocations.push({ lat: parseFloat(coords.lat), lng: parseFloat(coords.lng) });
        });
    } else if (offer.locations && offer.locations.length > 0) {
      // For cars, prioritize "going" location, then check all locations
      let goingLoc = offer.locations.find(loc => {
        const coords = getLocationCoords(loc);
        return loc.trip_direction === 'going' && coords.lat && coords.lng;
      });
      if (!goingLoc) {
        // If no "going" location, check all locations
        goingLoc = offer.locations.find(loc => {
          const coords = getLocationCoords(loc);
          return coords.lat && coords.lng;
        });
      }
      if (goingLoc) {
        const coords = getLocationCoords(goingLoc);
        offerLocations.push({ lat: parseFloat(coords.lat), lng: parseFloat(coords.lng) });
      }
    }
    
    if (offerLocations.length === 0) {
      console.log('❌ No offer locations found');
      return false;
    }
    
    console.log('📍 Offer locations:', offerLocations);
    
    // Check if any of user's pending requests match any offer location
    const matches = myPendingRequests.some(request => {
      console.log('🔍 Full request object:', JSON.stringify(request, null, 2));
      console.log('🔍 Request locations array:', request.locations);
      
      // Get coordinates - try multiple sources
      let requestLat = request.pickup_lat;
      let requestLng = request.pickup_lng;
      
      console.log('📍 Initial coordinates from pickup_lat/lng:', { requestLat, requestLng });
      
      // If not in pickup_lat/lng, get from locations array (prioritize "going" direction)
      if (!requestLat || !requestLng) {
        console.log('📍 pickup_lat/lng not available, checking locations array...');
        console.log('📍 Locations array length:', request.locations?.length);
        
        // Log each location in detail
        request.locations?.forEach((loc, idx) => {
          console.log(`📍 Location ${idx}:`, loc);
          console.log(`📍 Location ${idx} keys:`, Object.keys(loc));
          console.log(`📍 Location ${idx} values:`, {
            location_lat: loc.location_lat,
            location_lng: loc.location_lng,
            lat: loc.lat,
            lng: loc.lng,
            trip_direction: loc.trip_direction
          });
        });
        
        const goingLocation = request.locations?.find(loc => loc.trip_direction === 'going');
        const firstLocation = request.locations?.[0];
        const location = goingLocation || firstLocation;
        
        console.log('📍 Selected location (going or first):', location);
        console.log('📍 Selected location keys:', location ? Object.keys(location) : 'null');
        
        if (location) {
          requestLat = location.location_lat || location.lat;
          requestLng = location.location_lng || location.lng;
          console.log('📍 Coordinates from location:', { 
            requestLat, 
            requestLng, 
            location_lat: location.location_lat, 
            location_lng: location.location_lng,
            lat: location.lat,
            lng: location.lng,
            raw_location_lat_type: typeof location.location_lat,
            raw_location_lng_type: typeof location.location_lng,
            raw_lat_type: typeof location.lat,
            raw_lng_type: typeof location.lng
          });
        } else {
          console.log('❌ No location found in locations array');
        }
      }
      
      requestLat = parseFloat(requestLat);
      requestLng = parseFloat(requestLng);
      
      console.log('📍 Final parsed coordinates:', { requestLat, requestLng });
      
      if (!requestLat || !requestLng || isNaN(requestLat) || isNaN(requestLng)) {
        console.log('⚠️ Invalid request coordinates - request:', request);
        console.log('⚠️ Locations array:', request.locations);
        return false;
      }
      
      // Check if any offer location is within 10km of request location (increased from 5km for better matching)
      const match = offerLocations.some(offerLoc => {
        const distance = isWithinRadius(requestLat, requestLng, offerLoc.lat, offerLoc.lng, 10);
        console.log(`📍 Comparing request (${requestLat}, ${requestLng}) with offer (${offerLoc.lat}, ${offerLoc.lng}): within 10km = ${distance}`);
        return distance;
      });
      
      if (match) {
        console.log('✅ Offer matches request!');
      }
      return match;
    });
    
    console.log('🎯 Final match result for offer', offer.offer_id, ':', matches);
    return matches;
  }, [myPendingRequests]);

  // Loading state
  if (loading) {
    return (
      <div className="page-loading-overlay">
        <LoadingCar />
        <p className="loading-text">Loading event...</p>
      </div>
    );
  }

  // Error state
  if (error && !needsCode && !event) {
    return (
      <div className="event-dashboard-page">
        <div className="error-container">
          <div className="error-icon">😕</div>
          <h2>Event Not Found</h2>
          <p>This event doesn't exist or has been removed.</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Private event needs code
  if (needsCode) {
    return (
      <div className="event-code-page">
        <div className="event-code-card">
          <div className="event-code-logo">🔒</div>
          <h1 className="event-code-title">Private Event</h1>
          <p className="event-code-subtitle">
            <strong>{event?.event_name}</strong><br />
            Enter the access code to view this event
          </p>
          <form onSubmit={handleAccessCodeSubmit}>
            <div className="form-group">
              <input
                type="text"
                className="form-input"
                placeholder="Enter access code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                autoFocus
              />
              {error && <div className="form-error">{error}</div>}
            </div>
            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading}>
              {loading ? 'Checking...' : 'Unlock Event'}
            </button>
          </form>
          <div className="back-link">
            <a href="/">← Back to Home</a>
          </div>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="event-dashboard">
      {/* Shared Header Bar - Consistent across all pages */}
      <HeaderBar />

      {/* Notification Permission Banner - Only show to users with offers */}
      {userHasOffers && (
        <NotificationBanner
          isSupported={notificationsSupported}
          permission={notificationPermission}
          onRequestPermission={requestNotificationPermission}
        />
      )}

      {/* Event Header - Unified design */}
      <div className="event-page-header">
        {/* Background image or gradient */}
        <div className="event-page-header-bg">
          {event?.event_image && typeof event.event_image === 'string' && event.event_image.trim() && event.event_image !== 'null' && event.event_image !== 'undefined' ? (
            <>
              <img src={event.event_image} alt="" className="event-page-header-img" onError={(e) => { e.target.style.display = 'none'; }} />
              <div className="event-page-header-overlay" />
            </>
          ) : (
            <div className="event-page-header-gradient" />
          )}
        </div>

        {/* Theme-based floating decorations */}
        {event?.event_theme && eventThemeFloaters[event.event_theme] && (
          <div className="event-page-floaters" aria-hidden="true">
            {eventThemeFloaters[event.event_theme].map((f, i) => (
              <span
                key={i}
                className="event-page-floater"
                style={{
                  left: `${f.left}%`,
                  top: `${f.top}%`,
                  animationDelay: `${f.delay}s`,
                  fontSize: `clamp(1.2rem, ${(f.size || 1) * 3}vw, 2rem)`,
                }}
              >
                {f.emoji}
              </span>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="event-page-header-content">
          {/* Featured star - above badges */}
          {event?.is_featured && (
            <div className="event-page-featured-star">⭐</div>
          )}

          {/* Top row: badges + share */}
          <div className="event-page-header-top">
            <div className="event-page-badges">
              {event?.event_theme && eventThemeLabelKeys[event.event_theme] && (
                <span className="event-page-badge badge-type">
                  {eventThemeIcons[event.event_theme] || '📌'} {t('createEvent.' + eventThemeLabelKeys[event.event_theme])}
                </span>
              )}
              {event?.event_vibe && eventVibeLabelKeys[event.event_vibe] && (
                <span className="event-page-badge badge-vibe">{t('createEvent.' + eventVibeLabelKeys[event.event_vibe])}</span>
              )}
            </div>
            {eventCode && (
              <button type="button" className="event-page-share-btn" onClick={() => setShowShareMenu(!showShareMenu)} aria-label="Share">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              </button>
            )}
          </div>

          {/* Title */}
          <h1 className="event-page-title">{event?.event_name}</h1>

          {/* Inline meta row */}
          <div className="event-page-meta">
            {event?.event_date && (
              <span className="event-page-meta-item">
                📅 {new Date(event.event_date).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' })}
                {event?.event_time && ` · ${event.event_time}${event.event_end_time ? `–${event.event_end_time}` : ''}`}
              </span>
            )}
            {event?.event_location && (
              <span className="event-page-meta-item">📍 {event.event_location}</span>
            )}
            {event?.participant_count > 0 && (
              <span className="event-page-meta-item meta-participants">👥 {event.participant_count} {t('eventDashboard.joined')}</span>
            )}
            {event?.audience_size && audienceLabelKeys[event.audience_size] && (
              <span className="event-page-meta-item">{t('createEvent.' + audienceLabelKeys[event.audience_size])}</span>
            )}
          </div>

          {/* Description */}
          {event?.event_short_description && (
            <p className="event-page-desc">{event.event_short_description}</p>
          )}
          {event?.event_notes && (
            <p className="event-page-notes">📌 {event.event_notes}</p>
          )}

        </div>
      </div>

      {/* Floating Share Button - Mobile Only */}
      {eventCode && (
        <div className="floating-share-container">
          <button 
            type="button" 
            className="floating-share-btn"
            onClick={() => setShowShareMenu(!showShareMenu)}
            aria-label="Share"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </button>
          
          {showShareMenu && (
            <>
              <div 
                className="share-overlay"
                onClick={() => setShowShareMenu(false)}
              />
              <div className="share-dropdown-mobile">
                <div className="share-dropdown-header">
                  <span>{t('eventDashboard.share') || 'شارك الحدث'}</span>
                  <button onClick={() => setShowShareMenu(false)} className="share-close-btn">✕</button>
                </div>
                <div className="share-options">
                  <button onClick={copyEventLink} className="share-option">
                    <span className="share-icon">🔗</span>
                    <span>{t('eventDashboard.copyLink') || 'نسخ الرابط'}</span>
                  </button>
                  <button onClick={shareViaWhatsApp} className="share-option whatsapp">
                    <span className="share-icon">💬</span>
                    <span>{t('eventDashboard.shareWhatsApp') || 'واتساب'}</span>
                  </button>
                  <button onClick={shareViaTelegram} className="share-option telegram">
                    <span className="share-icon">✈️</span>
                    <span>{t('eventDashboard.shareTelegram') || 'تيليجرام'}</span>
                  </button>
                  {navigator.share && (
                    <button onClick={shareNative} className="share-option native">
                      <span className="share-icon">📤</span>
                      <span>{t('eventDashboard.shareMore') || 'المزيد...'}</span>
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Main Container - All content in same container */}
      <div className="event-content-container">
        {/* Legendary Tabs */}
        <div className="legendary-tabs">
          <button
            className={`legendary-tab ${activeTab === 'offers' ? 'active' : ''}`}
            onClick={() => setActiveTab('offers')}
          >
            🚗 {t('eventDashboard.ridesAvailable')} <span className="tab-count">{offers.length}</span>
          </button>
          <button
            className={`legendary-tab ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            🙋 {t('eventDashboard.ridesNeeded')} <span className="tab-count">{requests.length}</span>
          </button>
        </div>

        {/* My Confirmed Rides Section - Legendary (toggleable) */}
        {myJoinedRides.length > 0 && (
          <section className="confirmed-rides-section">
            <button
              type="button"
              className="confirmed-rides-title confirmed-rides-toggle"
              onClick={() => setConfirmedRidesExpanded(prev => !prev)}
              aria-expanded={confirmedRidesExpanded}
            >
              <span>✅ {t('eventDashboard.myConfirmedRides')} ({myJoinedRides.length})</span>
              <span className="confirmed-rides-chevron" aria-hidden>{confirmedRidesExpanded ? '▼' : '▶'}</span>
            </button>
            {confirmedRidesExpanded && (
            <div className="confirmed-rides-content">
              {myJoinedRides.map((ride, idx) => (
                <div key={ride.join_request_id || idx} className="confirmed-ride-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#1e293b', fontWeight: 700 }}>
                        🚗 {t('eventDashboard.ridingWith')} <strong>{ride.driverName}</strong>
                      </h3>
                      <div style={{ fontSize: '14px', color: '#64748b' }}>
                        <p style={{ margin: '4px 0' }}>📱 {ride.driverPhone}</p>
                        {ride.pickupLocation && (
                          <p style={{ margin: '4px 0' }}>📍 {t('eventDashboard.pickup')}: {ride.pickupLocation}</p>
                        )}
                        {ride.eventLocation && (
                          <p style={{ margin: '4px 0' }}>🎯 {t('eventDashboard.destination')}: {ride.eventLocation}</p>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a href={`tel:${ride.driverPhone}`} className="legendary-action-btn success" style={{ padding: '10px 16px' }}>
                        📞 {t('eventDashboard.call')}
                      </a>
                      <a
                        href={`https://wa.me/${formatPhone(ride.driverPhone)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="legendary-action-btn whatsapp"
                        style={{ padding: '10px 16px' }}
                        title="WhatsApp"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.54 4.142 1.546 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </a>
                    </div>
                  </div>

                  {ride.offerLocations?.[0]?.location_address && ride.eventLocation && ride.pickupLocation && ride.pickupLat && ride.pickupLng && (
                    <Suspense fallback={<LoadingPlaceholder />}>
                      <div style={{ marginTop: '12px' }}>
                        <RouteWithDetour
                          origin={ride.offerLocations[0].location_address}
                          destination={ride.eventLocation}
                          pickupLocation={{
                            address: ride.pickupLocation,
                            lat: ride.pickupLat,
                            lng: ride.pickupLng
                          }}
                          passengerName={ride.passengerName || ride.name}
                          height="200px"
                        />
                      </div>
                    </Suspense>
                  )}
                </div>
              ))}
            </div>
            )}
          </section>
        )}

        {/* Legendary Content */}
        <div className="legendary-content">
        {activeTab === 'offers' && (
          <>
            {/* Filter rides - full when offers exist, minimal header when none */}
            {offers.length > 0 ? (
              <div className="filters-section">
                <div className="filters-section-header">
                  <span className="filters-section-title">🔍 {t('eventDashboard.filterRides') || 'Filter rides'}</span>
                  <span className="filters-section-subtitle">{t('eventDashboard.narrowResults') || 'Narrow by trip type, seats, payment & more'}</span>
                </div>
                <QuickFilters
                  filters={filters}
                  onChange={setFilters}
                  sortValue={sortBy}
                  onSortChange={setSortBy}
                  showSort={true}
                />
                {Object.keys(filters).some(k => filters[k]) && (
                  <div className="filter-results-info">
                    {t('eventDashboard.showing')} {filteredOffers.length} {t('eventDashboard.of')} {offers.length} {t('eventDashboard.rides')}
                    <button className="clear-filters-btn" onClick={() => setFilters({})}>
                      {t('eventDashboard.clearFilters')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="filters-section filters-section-minimal">
                <span className="filters-section-title">🔍 {t('eventDashboard.filterRides') || 'Filter rides'}</span>
              </div>
            )}

            {/* Offer a Ride / Request a Ride - under filter rides, before offers */}
            <div className="quick-action-buttons event-page-ride-actions">
              <button
                className="quick-action-btn offer-btn"
                onClick={() => navigate(isDemonstrations ? `/dems/event/${eventCode}/publish?mode=offer` : `/event/${eventCode}/publish?mode=offer`)}
              >
                🚗 {t('eventDashboard.offerRide') || 'Offer a Ride'}
              </button>
              <button
                className="quick-action-btn request-btn"
                onClick={() => navigate(isDemonstrations ? `/dems/event/${eventCode}/publish?mode=request` : `/event/${eventCode}/publish?mode=request`)}
              >
                🙋 {t('eventDashboard.requestRide') || 'Request a Ride'}
              </button>
            </div>

            <div className="legendary-cards-grid">
              {filteredOffers.length === 0 && offers.length > 0 ? (
                <div className="legendary-empty-state">
                  <span className="legendary-empty-icon">🔍</span>
                  <h3>{t('eventDashboard.noRidesMatchFilters')}</h3>
                  <p>{t('eventDashboard.adjustFilters')}</p>
                  <button onClick={() => setFilters({})} className="legendary-btn legendary-btn-secondary">
                    {t('eventDashboard.clearFilters')}
                  </button>
                </div>
              ) : offers.length === 0 ? (
                <div className="legendary-empty-state">
                  <span className="legendary-empty-icon">🚗</span>
                  <h3>{t('eventDashboard.noRidesOffered')}</h3>
                  <p>{t('eventDashboard.beFirstToOffer')}</p>
                  <button onClick={() => navigate(isDemonstrations ? `/dems/event/${eventCode}/publish?mode=offer` : `/event/${eventCode}/publish?mode=offer`)} className="legendary-btn legendary-btn-primary">
                    {t('eventDashboard.offerRide')}
                  </button>
                </div>
              ) : (
                filteredOffers.map((offer, index) => {
                const seats = getSeatsDisplay(offer);
                const isMine = isMyOffer(offer);
                const isFull = seats.available <= 0
                  || (offer.available_seats !== undefined && Number(offer.available_seats) <= 0)
                  || (seats.total > 0 && seats.confirmed >= seats.total);
                const isMatching = isMatchingOffer(offer);
                const isConfirmedRider = myJoinedRides.some(ride => ride.offer_id === offer.offer_id);
                const canContactDriver = !offer.hide_phone || isConfirmedRider;

                // Get short location (city only)
                const getShortLocation = (locations) => {
                  if (!locations || locations.length === 0) return '';
                  const loc = locations.find(l => l.location_type === 'pickup' || l.trip_direction === 'going') || locations[0];
                  const addr = loc?.nickname || loc?.location_address || '';
                  if (!addr) return '';
                  const parts = addr.split(',');
                  return parts[0]?.trim() || addr;
                };

                // Get trip type display
                const getTripTypeDisplay = () => {
                  const pickups = offer.locations?.filter(l => l.location_type === 'pickup' || l.trip_direction === 'going') || [];
                  const dropoffs = offer.locations?.filter(l => l.location_type === 'dropoff' || l.trip_direction === 'return') || [];
                  if (pickups.length > 0 && dropoffs.length > 0) return '↔️';
                  if (pickups.length > 0) return '→';
                  if (dropoffs.length > 0) return '←';
                  return '↔️';
                };

                // Get price display
                const getPriceDisplay = () => {
                  if (offer.payment_required === 'obligatory' && offer.payment_amount) {
                    return `₪${offer.payment_amount}`;
                  }
                  if (offer.payment_required === 'optional') {
                    return t('eventDashboard.tipWelcome') || 'Tip';
                  }
                  return t('eventDashboard.free') || 'مجاني';
                };

                // Get preference label
                const pref = offer.preference === 'male' ? { icon: '👨', text: t('publishRide.men') || 'رجال' } 
                           : offer.preference === 'female' ? { icon: '👩', text: t('publishRide.women') || 'نساء' } 
                           : null;

                // Get stops counts
                const goingStops = offer.locations?.filter(l => l.trip_direction === 'going') || [];
                const returnStops = offer.locations?.filter(l => l.trip_direction === 'return') || [];

                return (
                  <div 
                    key={offer.offer_id || index} 
                    className={`modern-offer-card ${isMine ? 'my-card' : ''} ${isFull ? 'full-card' : ''} ${isMatching ? 'matching-offer' : ''}`}
                  >
                    {/* Status Badges - Top Right */}
                    <div className="modern-card-badges">
                      {isMine && <span className="modern-badge mine">{t('eventDashboard.myOffer') || 'عرضي'}</span>}
                      {isFull && !isMine && <span className="modern-badge full">{t('eventDashboard.full') || 'مكتمل'}</span>}
                      {isMatching && !isMine && <span className="modern-badge matching">✨ {t('eventDashboard.matchesYourRequest') || 'يناسبك!'}</span>}
                    </div>

                    {/* Card Header - Driver Info */}
                    <div className="modern-card-header">
                      <div className="modern-avatar">
                        <span className="avatar-icon">{offer.vehicle_type === 'public' ? '🚌' : '🚗'}</span>
                      </div>
                      <div className="modern-driver-info">
                        <h3 className="driver-name">{offer.driver_name || offer.name || t('eventDashboard.driver')}</h3>
                        {offer.arrival_time && (
                          <span className="driver-time">🕐 {offer.arrival_time}</span>
                        )}
                      </div>
                    </div>

                    {/* Departure Location - Prominent */}
                    {getShortLocation(offer.locations) && (
                      <div className="modern-departure">
                        <span className="departure-icon">📍</span>
                        <span className="departure-text">{getShortLocation(offer.locations)}</span>
                      </div>
                    )}

                    {/* Info Pills Row */}
                    <div className="modern-pills">
                      {/* Seats Pill */}
                      <div className={`modern-pill seats ${isFull ? 'full' : seats.available <= 2 ? 'low' : 'available'}`}>
                        <span className="pill-icon">💺</span>
                        <span className="pill-value">{seats.available}</span>
                        <span className="pill-label">/ {seats.total}</span>
                      </div>

                      {/* Trip Direction Pill with Stops Count - Clickable */}
                      <div 
                        className={`modern-pill direction clickable ${(goingStops.length > 0 || returnStops.length > 0) ? 'has-stops' : ''}`}
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          if (goingStops.length > 0 || returnStops.length > 0) {
                            // Toggle stops visibility
                            const card = e.target.closest('.modern-offer-card');
                            const stopsEl = card?.querySelector('.modern-stops-detail');
                            if (stopsEl) {
                              stopsEl.classList.toggle('show');
                            }
                          }
                        }}
                      >
                        {getTripTypeDisplay() === '↔️' ? (
                          <>
                            <span className="pill-direction-group">
                              <span className="direction-arrow">→</span>
                              <span className="direction-label">{t('publishRide.going') || 'ذهاب'}</span>
                              {goingStops.length > 0 && <span className="stops-count">🚏{goingStops.length}</span>}
                            </span>
                            <span className="direction-separator">|</span>
                            <span className="pill-direction-group">
                              <span className="direction-arrow">←</span>
                              <span className="direction-label">{t('publishRide.return') || 'إياب'}</span>
                              {returnStops.length > 0 && <span className="stops-count">🚏{returnStops.length}</span>}
                            </span>
                          </>
                        ) : getTripTypeDisplay() === '→' ? (
                          <span className="pill-direction-group">
                            <span className="direction-arrow">→</span>
                            <span className="direction-label">{t('publishRide.going') || 'ذهاب'}</span>
                            {goingStops.length > 0 && <span className="stops-count">🚏{goingStops.length}</span>}
                          </span>
                        ) : (
                          <span className="pill-direction-group">
                            <span className="direction-arrow">←</span>
                            <span className="direction-label">{t('publishRide.return') || 'إياب'}</span>
                            {returnStops.length > 0 && <span className="stops-count">🚏{returnStops.length}</span>}
                          </span>
                        )}
                        {(goingStops.length > 0 || returnStops.length > 0) && <span className="expand-arrow">▼</span>}
                      </div>

                      {/* Price Pill */}
                      <div className={`modern-pill price ${offer.payment_required === 'not_required' ? 'free' : offer.payment_required === 'obligatory' ? 'paid' : 'tip'}`}>
                        <span className="pill-icon">{offer.payment_required === 'not_required' ? '🆓' : '💰'}</span>
                        <span className="pill-label">{getPriceDisplay()}</span>
                      </div>

                      {/* Gender Preference Pill */}
                      {pref && (
                        <div className={`modern-pill gender ${offer.preference}`}>
                          <span className="pill-icon">{pref.icon}</span>
                          <span className="pill-label">{pref.text}</span>
                        </div>
                      )}

                      {/* WhatsApp Only Pill */}
                      {offer.disable_join_request && (
                        <div className="modern-pill whatsapp-only">
                          <svg className="whatsapp-icon-small" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                          </svg>
                          <span className="pill-label">{t('eventDashboard.whatsappOnly') || 'واتساب فقط'}</span>
                        </div>
                      )}
                    </div>

                    {/* Stops Detail - Hidden by default, shows on click */}
                    {(goingStops.length > 0 || returnStops.length > 0) && (
                      <div className="modern-stops-detail">
                        {goingStops.length > 0 && (
                          <div className="stops-section going">
                            <div className="stops-header">→ {t('publishRide.going') || 'ذهاب'}</div>
                            {goingStops.map((stop, i) => (
                              <div key={i} className="stop-item">
                                <span className="stop-number">{i + 1}</span>
                                <span className="stop-name">{stop.nickname || stop.location_address?.split(',')[0]}</span>
                                {stop.stop_time && <span className="stop-time">{stop.stop_time}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        {returnStops.length > 0 && (
                          <div className="stops-section return">
                            <div className="stops-header">← {t('publishRide.return') || 'إياب'}</div>
                            {returnStops.map((stop, i) => (
                              <div key={i} className="stop-item">
                                <span className="stop-number">{i + 1}</span>
                                <span className="stop-name">{stop.nickname || stop.location_address?.split(',')[0]}</span>
                                {stop.stop_time && <span className="stop-time">{stop.stop_time}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Description Preview */}
                    {offer.description && (
                      <p className="modern-description">{offer.description.length > 80 ? offer.description.substring(0, 80) + '...' : offer.description}</p>
                    )}

                    {/* Action Buttons */}
                    {!(isFull && !isMine) && (
                      <div className="modern-actions">
                        {isMine ? (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); handleViewJoinRequests(offer); }} className="modern-btn primary">
                              <span className="btn-icon">📋</span>
                              <span className="btn-text">{t('eventDashboard.requests') || 'الطلبات'}</span>
                              {offer.pending_requests > 0 && <span className="btn-badge">{offer.pending_requests}</span>}
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleEdit('offer', offer); }} className="modern-btn edit">
                              <span className="btn-icon">✏️</span>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDelete('offer', offer); }} className="modern-btn delete">
                              <span className="btn-icon">🗑️</span>
                            </button>
                          </>
                        ) : (
                          <>
                            {seats.available > 0 && !offer.disable_join_request && (
                              <button onClick={(e) => { e.stopPropagation(); handleOpenJoinModal(offer); }} className="modern-btn join">
                                <span className="btn-icon">🙋</span>
                                <span className="btn-text">{t('eventDashboard.requestToJoin') || 'طلب انضمام'}</span>
                              </button>
                            )}
                            {canContactDriver && (
                              <>
                                <button onClick={(e) => { e.stopPropagation(); handleContactClick(offer, 'whatsapp'); }} className="modern-btn whatsapp">
                                  <svg className="whatsapp-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                  </svg>
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); handleContactClick(offer, 'phone'); }} className="modern-btn phone">
                                  <span className="btn-icon">📞</span>
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Confirmed Passengers Count - Only for owner */}
                    {isMine && offer.confirmed_passengers && offer.confirmed_passengers.length > 0 && (
                      <div className="modern-passengers-summary" onClick={(e) => { e.stopPropagation(); setShowPassengersModal(offer); }}>
                        <span className="passengers-icon">✅</span>
                        <span className="passengers-text">
                          {offer.confirmed_passengers.reduce((sum, p) => sum + (p.passenger_count || 1), 0)} {t('eventDashboard.confirmed') || 'مؤكد'}
                        </span>
                        <span className="passengers-action">⚙️</span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
            </div>
          </>
        )}

        {activeTab === 'requests' && (
          <>
            {/* Quick Action Buttons */}
            {requests.length > 0 && (
              <div className="quick-action-buttons">
                <button 
                  className="quick-action-btn request-btn"
                  onClick={() => navigate(isDemonstrations ? `/dems/event/${eventCode}/publish?mode=request` : `/event/${eventCode}/publish?mode=request`)}
                >
                  🙋 {t('eventDashboard.requestRide') || 'Request a Ride'}
                </button>
                <button 
                  className="quick-action-btn offer-btn"
                  onClick={() => navigate(isDemonstrations ? `/dems/event/${eventCode}/publish?mode=offer` : `/event/${eventCode}/publish?mode=offer`)}
                >
                  🚗 {t('eventDashboard.offerRide') || 'Offer a Ride'}
                </button>
              </div>
            )}

            <div className="legendary-cards-grid">
              {requests.length === 0 ? (
                <div className="legendary-empty-state">
                  <span className="legendary-empty-icon">🙋</span>
                  <h3>No ride requests yet</h3>
                  <p>Looking for a ride? Post a request!</p>
                  <button onClick={() => navigate(isDemonstrations ? `/dems/event/${eventCode}/publish?mode=request` : `/event/${eventCode}/publish?mode=request`)} className="legendary-btn legendary-btn-primary">
                    Request a Ride
                  </button>
                </div>
              ) : (
              requests.map((request, index) => {
                const isMine = isMyRequest(request);
                const hasMatch = request.matched_offer;
                const pref = getPreferenceLabel(request.preference);
                const canContactPassenger = !request.hide_phone;

                return (
                  <div key={request.request_id || index} className={`legendary-card request-card ${isMine ? 'my-card' : ''} ${hasMatch ? 'matched-card' : ''}`}>
                    {isMine && <div className="legendary-badge my-badge">My Request</div>}
                    {hasMatch && <div className="legendary-badge matched-badge">✅ Matched</div>}

                    <div className="legendary-card-header">
                      <div className="legendary-driver-info">
                        <div className="legendary-avatar passenger">🙋</div>
                        <div className="legendary-driver-details">
                          <h3>{request.name || 'Passenger'}</h3>
                          <div className="legendary-meta-badges">
                            <span className="legendary-seats-badge">
                              {request.trip_type === 'both' ? '🔄 Round trip' : request.trip_type === 'going' ? '→ Going' : '← Return'}
                            </span>
                            {pref && (
                              <span className={`legendary-pref-badge ${request.preference}`}>
                                {pref.icon} {pref.text}
                              </span>
                            )}
                            {request.arrival_time && (
                              <span className="legendary-arrival-badge">
                                🕐 {t('eventDashboard.arriving') || 'Arriving'}: {request.arrival_time}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {request.locations && request.locations.length > 0 && (
                      <div className="legendary-locations">
                        {request.locations.map((loc, i) => (
                          <div key={i} className="legendary-location-item">
                            <span className={`legendary-direction-badge ${loc.trip_direction}`}>
                              {loc.trip_direction === 'going' ? '→ Going' : '← Return'}
                            </span>
                            <span>{loc.location_address}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {hasMatch && (
                      <div className="legendary-match-info">
                        <div className="legendary-match-label">🚗 Riding with</div>
                        <div className="legendary-match-driver">{request.matched_offer.driver_name}</div>
                        {isMine && (
                          <a
                            href={`https://wa.me/${formatPhone(request.matched_offer.driver_phone)}?text=Hi! I'm confirmed for your carpool to ${event?.event_name}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="legendary-action-btn whatsapp"
                            style={{ marginTop: '12px', display: 'inline-flex', minWidth: 'auto', width: 'auto', padding: '12px 18px' }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                            {t('eventDashboard.contactDriver') || 'Contact Driver'}
                          </a>
                        )}
                      </div>
                    )}

                    <div className="legendary-actions">
                      {isMine ? (
                        <>
                          <button onClick={() => handleEdit('request', request)} className="legendary-action-btn secondary">
                            ✏️ {t('common.edit')}
                          </button>
                          <button onClick={() => handleDelete('request', request)} className="legendary-action-btn ghost">
                            🗑️
                          </button>
                        </>
                      ) : (
                        <>
                          {!hasMatch && (
                            <button
                              onClick={() => {
                                if (myOffers.length === 0) {
                                  showToast('Create an offer first', 'info');
                                  navigate(isDemonstrations ? `/dems/event/${eventCode}/publish?mode=offer` : `/event/${eventCode}/publish?mode=offer`);
                                  return;
                                }
                                handleOpenInviteModal(request);
                              }}
                              className="legendary-action-btn primary"
                            >
                              🚗 {t('eventDashboard.offerRide') || 'Offer Ride'}
                            </button>
                          )}
                          {canContactPassenger && (
                            <>
                              <a
                                href={`https://wa.me/${formatPhone(request.phone)}?text=Hi! I can give you a ride to ${event?.event_name}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="legendary-action-btn whatsapp"
                                title={t('eventDashboard.whatsapp') || 'WhatsApp'}
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                </svg>
                              </a>
                              <a href={`tel:${request.phone}`} className="legendary-action-btn secondary">
                                📞
                              </a>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            </div>
          </>
        )}
      </div>
      </div>

      {/* Publish/Edit Modal - Legendary */}
      {(showModal === 'offer' || showModal === 'request' || showModal === 'edit-offer' || showModal === 'edit-request') && (
        <div className="legendary-modal-overlay" onClick={() => !submitting && setShowModal(null)}>
          <div className="legendary-modal" onClick={e => e.stopPropagation()}>
            <div className="legendary-modal-handle" />
            <div className="legendary-modal-header">
              <h2>
                {showModal === 'edit-offer' ? `✏️ ${t('eventDashboard.editOffer') || 'تعديل العرض'}` :
                 showModal === 'edit-request' ? `✏️ ${t('eventDashboard.editRequest') || 'تعديل الطلب'}` :
                 showModal === 'offer' ? `🚗 ${t('eventDashboard.offerRide')}` : `🙋 ${t('eventDashboard.needRide')}`}
              </h2>
              <button className="legendary-modal-close" onClick={() => !submitting && setShowModal(null)}>×</button>
            </div>
            
            <div className="legendary-modal-body">
              {/* OTP Verification Step */}
              {/* OTP step removed - direct login/register */}
                <>
                  {/* Logged in notice */}
                  {isAuthenticated && (
                    <div className="info-banner success">
                      <span className="info-banner-icon">✅</span>
                      <span>Logged in - Your info is pre-filled</span>
                    </div>
                  )}

                  {/* SECTION 1: Personal Info - Compact */}
<div className="form-section">
  <div className="form-section-header">
    <span className="form-section-icon">👤</span>
    <h4 className="form-section-title">Your Details</h4>
  </div>

  <div className="form-group">
    <div className="form-field-header">
      <label className="form-label">Name</label>
      <label className="inline-toggle">
        <input type="checkbox" name="hideName" checked={formData.hideName} onChange={handleInputChange} />
        <span>Hide</span>
      </label>
    </div>
    <input type="text" name="name" className="form-input" value={formData.name} onChange={handleInputChange} placeholder="Your name" />
  </div>

  <div className="form-row">
    <div className="form-group">
      <div className="form-field-header">
        <label className="form-label">Phone</label>
        <label className="inline-toggle">
          <input type="checkbox" name="hidePhone" checked={formData.hidePhone} onChange={handleInputChange} />
          <span>Hide</span>
        </label>
      </div>
      <input type="tel" name="phone" className="form-input" value={formData.phone} onChange={handleInputChange} placeholder="+972..." />
    </div>
    <div className="form-group">
      <div className="form-field-header">
        <label className="form-label">Email</label>
        <label className="inline-toggle">
          <input type="checkbox" name="hideEmail" checked={formData.hideEmail} onChange={handleInputChange} />
          <span>Hide</span>
        </label>
      </div>
      <input type="email" name="email" className="form-input" value={formData.email} onChange={handleInputChange} placeholder="email@..." />
    </div>
  </div>

  {/* Compact Gender Pills */}
  <div className="form-group">
    <label className="form-label">Gender</label>
    <div className="pill-toggle-group">
      <label className={`pill-toggle gender-male ${formData.gender === 'male' ? 'selected' : ''}`}>
        <input type="radio" name="gender" value="male" checked={formData.gender === 'male'} onChange={handleInputChange} />
        <span className="pill-icon">♂️</span>
        <span>Male</span>
      </label>
      <label className={`pill-toggle gender-female ${formData.gender === 'female' ? 'selected' : ''}`}>
        <input type="radio" name="gender" value="female" checked={formData.gender === 'female'} onChange={handleInputChange} />
        <span className="pill-icon">♀️</span>
        <span>Female</span>
      </label>
    </div>
  </div>

  {(formData.hideName || formData.hidePhone || formData.hideEmail) && (
    <div className="info-banner warning">
      <span className="info-banner-icon">🔒</span>
      <span>Hidden info shared after confirmation</span>
    </div>
  )}
</div>

{/* SECTION 3: Location - Compact */}
<div className="form-section">
  <div className="form-section-header">
    <span className="form-section-icon">📍</span>
    <h4 className="form-section-title">Pickup Location</h4>
  </div>

  <div className="form-group">
    <Suspense fallback={<input type="text" className="form-input" placeholder="Loading..." disabled />}>
      <MapLocationPicker
        value={formData.pickupLocation}
        onChange={(value) => setFormData(prev => ({ ...prev, pickupLocation: value }))}
        onLocationSelect={(loc) => setFormData(prev => ({ ...prev, pickupLocation: loc.address, pickupLat: loc.lat, pickupLng: loc.lng }))}
        placeholder="Search location..."
        disabled={false}
      />
    </Suspense>
  </div>

  {formData.pickupLat && formData.pickupLng && event?.event_location && (
    <Suspense fallback={<LoadingPlaceholder />}>
      <div className="map-preview-compact">
        <RouteMap
          origin={{ address: formData.pickupLocation, lat: formData.pickupLat, lng: formData.pickupLng }}
          destination={
            event.location_lat && event.location_lng
              ? { address: event.event_location, lat: event.location_lat, lng: event.location_lng }
              : event.event_location
          }
          height="150px"
          showDirections={true}
          onRouteCalculated={handleRouteCalculated}
        />
      </div>
      {routeDistance && (
        <div className="info-banner info">
          <span className="info-banner-icon">📏</span>
          <span>{routeDistance.toFixed(1)} km • Max ₪{maxPayment.toFixed(0)}</span>
        </div>
      )}
    </Suspense>
  )}

  {/* Return location toggle */}
  {(formData.tripType === 'both' || formData.tripType === 'return') && (
    <>
      <label className="inline-toggle" style={{ marginTop: '10px', padding: '8px 0' }}>
        <input type="checkbox" name="sameReturnLocation" checked={formData.sameReturnLocation} onChange={handleInputChange} />
        <span>Same location for return</span>
      </label>

      {!formData.sameReturnLocation && (
        <div className="form-group" style={{ marginTop: '10px' }}>
          <Suspense fallback={<input type="text" className="form-input" placeholder="Loading..." />}>
            <MapLocationPicker
              value={formData.returnLocation}
              onChange={(value) => setFormData(prev => ({ ...prev, returnLocation: value }))}
              onLocationSelect={(loc) => setFormData(prev => ({ ...prev, returnLocation: loc.address, returnLat: loc.lat, returnLng: loc.lng }))}
              placeholder="Return pickup..."
            />
          </Suspense>
        </div>
      )}
    </>
  )}
</div>

  {/* SECTION 4: Preferences - Compact */}
<div className="form-section">
  <div className="form-section-header">
    <span className="form-section-icon">⚙️</span>
    <h4 className="form-section-title">Preferences</h4>
  </div>

  {/* Car notes - only for offers */}
  {(showModal === 'offer' || showModal === 'edit-offer') && (
    <div className="form-group">
      <label className="form-label">Notes</label>
      <input type="text" name="description" className="form-input" value={formData.description} onChange={handleInputChange} placeholder="Blue Toyota, no smoking..." />
    </div>
  )}

  {/* Who can join - Compact Pills */}
  <div className="form-group">
    <label className="form-label">Who can join?</label>
    <div className="pill-toggle-group">
      <label className={`pill-toggle ${formData.preference === 'any' ? 'selected' : ''}`}>
        <input type="radio" name="preference" value="any" checked={formData.preference === 'any'} onChange={handleInputChange} />
        <span className="pill-icon">👥</span>
        <span>Anyone</span>
      </label>
      <label className={`pill-toggle gender-male ${formData.preference === 'male' ? 'selected' : ''}`}>
        <input type="radio" name="preference" value="male" checked={formData.preference === 'male'} onChange={handleInputChange} />
        <span className="pill-icon">♂️</span>
        <span>Males</span>
      </label>
      <label className={`pill-toggle gender-female ${formData.preference === 'female' ? 'selected' : ''}`}>
        <input type="radio" name="preference" value="female" checked={formData.preference === 'female'} onChange={handleInputChange} />
        <span className="pill-icon">♀️</span>
        <span>Females</span>
      </label>
    </div>
  </div>
</div>
                  {/* SECTION 5: Payment - Only for offers */}
                  {(showModal === 'offer' || showModal === 'edit-offer') && (
                    <div className="form-section" style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)', borderColor: '#e9d5ff' }}>
                      <div className="form-section-header">
                        <span className="form-section-icon">💰</span>
                        <h4 className="form-section-title" style={{ color: '#7c3aed' }}>Payment</h4>
                      </div>

                      {/* Payment type cards */}
                      <div className="form-group">
                        <div className="option-cards">
                          <label className={`option-card ${formData.paymentRequired === 'not_required' ? 'selected' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, paymentRequired: 'not_required', paymentMethod: '' })); }}>
                            <input type="radio" name="paymentRequired" value="not_required" checked={formData.paymentRequired === 'not_required'} onChange={() => {}} />
                            <div className="option-card-icon">🆓</div>
                            <div className="option-card-label">Free</div>
                          </label>
                          <label className={`option-card ${formData.paymentRequired === 'optional' ? 'selected' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, paymentRequired: 'optional', paymentAmount: maxPayment > 0 ? maxPayment.toFixed(0) : '' })); }}>
                            <input type="radio" name="paymentRequired" value="optional" checked={formData.paymentRequired === 'optional'} onChange={() => {}} />
                            <div className="option-card-icon">🙏</div>
                            <div className="option-card-label">Optional</div>
                          </label>
                          <label className={`option-card ${formData.paymentRequired === 'obligatory' ? 'selected' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, paymentRequired: 'obligatory', paymentAmount: maxPayment > 0 ? maxPayment.toFixed(0) : '' })); }}>
                            <input type="radio" name="paymentRequired" value="obligatory" checked={formData.paymentRequired === 'obligatory'} onChange={() => {}} />
                            <div className="option-card-icon">💳</div>
                            <div className="option-card-label">Required</div>
                          </label>
                        </div>
                      </div>

                      {formData.paymentRequired !== 'not_required' && (
                        <>
                          {/* Amount input */}
                          <div className="form-group">
                            <label className="form-label" style={{ color: '#7c3aed' }}>Price per passenger</label>
                            <div className="payment-amount-input">
                              <span className="currency">₪</span>
                              <input
                                type="number"
                                name="paymentAmount"
                                className="form-input"
                                value={formData.paymentAmount}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value);
                                  if (maxPayment > 0 && val > maxPayment) {
                                    setFormData(prev => ({ ...prev, paymentAmount: maxPayment.toFixed(0) }));
                                  } else {
                                    handleInputChange(e);
                                  }
                                }}
                                placeholder={maxPayment > 0 ? maxPayment.toFixed(0) : "0"}
                                min="0"
                                max={maxPayment > 0 ? maxPayment : undefined}
                                step="1"
                              />
                            </div>
                            <p className="payment-max-hint">
                              {routeDistance ? `Max: ₪${maxPayment.toFixed(0)} (${routeDistance.toFixed(1)}km)` : 'Select location to see max'}
                            </p>
                          </div>

                          {/* Payment method */}
                          <div className="form-group">
                            <label className="form-label" style={{ color: '#7c3aed' }}>Payment Method</label>
                            <div className="option-cards">
                              <label className={`option-card ${formData.paymentMethod === 'bit' ? 'selected' : ''}`}>
                                <input type="radio" name="paymentMethod" value="bit" checked={formData.paymentMethod === 'bit'} onChange={handleInputChange} />
                                <div className="option-card-icon">📱</div>
                                <div className="option-card-label">Bit</div>
                              </label>
                              <label className={`option-card ${formData.paymentMethod === 'paybox' ? 'selected' : ''}`}>
                                <input type="radio" name="paymentMethod" value="paybox" checked={formData.paymentMethod === 'paybox'} onChange={handleInputChange} />
                                <div className="option-card-icon">📦</div>
                                <div className="option-card-label">PayBox</div>
                              </label>
                              <label className={`option-card ${formData.paymentMethod === 'cash' ? 'selected' : ''}`}>
                                <input type="radio" name="paymentMethod" value="cash" checked={formData.paymentMethod === 'cash'} onChange={handleInputChange} />
                                <div className="option-card-icon">💵</div>
                                <div className="option-card-label">Cash</div>
                              </label>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Remember me */}
                  {!editingItem && !isAuthenticated && (
                    <label className="inline-toggle" style={{ padding: '12px 0' }}>
                      <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                      <span>💾 Remember my details</span>
                    </label>
                  )}
                </>
            </div>
            
            <div className="legendary-modal-footer">
                <button
                  className="legendary-action-btn secondary"
                  onClick={() => { setShowModal(null); resetForm(); }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="legendary-action-btn primary"
                  onClick={() => handlePublish(showModal.includes('offer') ? 'offer' : 'request')}
                  disabled={submitting}
                >
                  {submitting ? (
                    <span className="btn-car-loading">
                      <span className="mini-car">🚗</span>
                      {isAuthenticated ? (t('common.saving') || 'جاري الحفظ...') : (t('login.sendingOTP') || 'Sending code...')}
                    </span>
                  ) : (editingItem ? (t('eventDashboard.saveChanges') || 'حفظ التغييرات') : (showModal === 'offer' ? `🚀 ${t('publishRide.publishRide')}` : `🚀 ${t('publishRide.sendRequest')}`))}
                </button>
              </div>
            
            {/* Full screen car animation while submitting */}
            {submitting && (
              <div className="page-loading-overlay">
                <LoadingCar />
                <p className="loading-text">{showModal.includes('offer') ? 'Creating your ride offer...' : 'Sending your request...'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Join Ride Modal - Legendary */}
      {showModal === 'join-ride' && selectedOffer && (
        <div className="legendary-modal-overlay" onClick={() => { if (!submitting) { setShowModal(null); setJoinPickupLocation(null); setJoinDropoffLocation(null); setUseCustomPickup(false); } }}>
          <div className="legendary-modal" onClick={e => e.stopPropagation()}>
            <div className="legendary-modal-handle" />
            <div className="legendary-modal-header">
              <h2>🙋 Request to Join Ride</h2>
              <button className="legendary-modal-close" onClick={() => { if (!submitting) { setShowModal(null); setJoinPickupLocation(null); setJoinDropoffLocation(null); setUseCustomPickup(false); } }}>×</button>
            </div>

            <div className="legendary-modal-body">
              <div className="driver-preview">
                <h3>{selectedOffer.vehicle_type === 'public' ? '🚌' : '🚗'} {selectedOffer.driver_name || selectedOffer.name}</h3>
                <p>{selectedOffer.locations?.[0]?.location_address}</p>
                {selectedOffer.preference && selectedOffer.preference !== 'any' && (
                  <span className={`preference-badge ${selectedOffer.preference}`} style={{ marginTop: '8px', display: 'inline-block' }}>
                    {selectedOffer.preference === 'male' ? '♂️ Males preferred' : '♀️ Females preferred'}
                  </span>
                )}
              </div>
              
              {/* Bus/Public Vehicle - Enhanced visual selection */}
              {selectedOffer.vehicle_type === 'public' ? (
                <div className="bus-location-selection">
                  {(() => {
                    const pickups = selectedOffer.locations?.filter(l => l.location_type === 'pickup' || l.trip_direction === 'going') || [];
                    const dropoffs = selectedOffer.locations?.filter(l => l.location_type === 'dropoff' || l.trip_direction === 'return') || [];

                    return (
                      <>
                        {/* Visual Route Map */}
                        <div className="bus-route-preview">
                          <Suspense fallback={
                            <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', borderRadius: '12px' }}>
                              <span>Loading map...</span>
                            </div>
                          }>
                            <BusRouteVisualizer
                              pickupStops={pickups.map(p => ({
                                address: p.location_address,
                                lat: p.location_lat || null,
                                lng: p.location_lng || null,
                                nickname: p.nickname,
                                time: p.stop_time
                              })).filter(p => p.lat && p.lng)}
                              dropoffStops={dropoffs.map(d => ({
                                address: d.location_address,
                                lat: d.location_lat || null,
                                lng: d.location_lng || null,
                                nickname: d.nickname,
                                time: d.stop_time
                              })).filter(d => d.lat && d.lng)}
                              eventLocation={event && event.location_lat && event.location_lng ? { lat: event.location_lat, lng: event.location_lng } : null}
                              eventName={event?.event_name}
                              height={200}
                              showLabels={true}
                            />
                          </Suspense>
                        </div>

                        {/* Pickup Stops - Visual Cards */}
                        <div className="bus-stops-selection">
                          <label className="form-label">📍 {t('eventDashboard.selectPickup') || 'Select Pickup Stop'} *</label>
                          <div className="bus-stop-cards">
                            {pickups.map((loc, i) => {
                              // Use index as unique identifier to ensure only one location is selected
                              const isSelected = joinPickupLocation && 
                                !useCustomPickup &&
                                joinPickupLocation._pickupIndex === i;
                              
                              return (
                                <div 
                                  key={`pickup-${i}-${loc.location_id || i}-${loc.location_address}`}
                                  className={`bus-stop-card ${isSelected ? 'selected' : ''}`}
                                  onClick={() => {
                                    setUseCustomPickup(false);
                                    // Store the index along with location data for unique identification
                                    setJoinPickupLocation({
                                      address: loc.location_address,
                                      lat: loc.location_lat,
                                      lng: loc.location_lng,
                                      _pickupIndex: i // Store index for comparison
                                    });
                                    // Auto-select matching dropoff
                                    const matchingDropoff = dropoffs.find(d => d.location_address === loc.location_address);
                                    if (matchingDropoff) {
                                      const dropoffIndex = dropoffs.findIndex(d => d.location_address === matchingDropoff.location_address);
                                      setJoinDropoffLocation({
                                        address: matchingDropoff.location_address,
                                        lat: matchingDropoff.location_lat,
                                        lng: matchingDropoff.location_lng,
                                        _dropoffIndex: dropoffIndex
                                      });
                                    } else if (dropoffs.length > 0 && !joinDropoffLocation) {
                                      setJoinDropoffLocation({
                                        address: dropoffs[0].location_address,
                                        lat: dropoffs[0].location_lat,
                                        lng: dropoffs[0].location_lng,
                                        _dropoffIndex: 0
                                      });
                                    }
                                  }}
                                >
                                  <div className="bus-stop-card-header">
                                    <span className="bus-stop-number pickup">{i + 1}</span>
                                    {loc.stop_time && (
                                      <span className="bus-stop-time">🕐 {loc.stop_time}</span>
                                    )}
                                  </div>
                                  <div className="bus-stop-card-body">
                                    {loc.nickname && (
                                      <span className="bus-stop-nickname">{loc.nickname}</span>
                                    )}
                                    <span className="bus-stop-address">{loc.location_address}</span>
                                  </div>
                                  {isSelected && (
                                    <div className="bus-stop-selected-badge">✓</div>
                                  )}
                                </div>
                              );
                            })}
                            {selectedOffer.allow_custom_pickup && (
                              <div 
                                className={`bus-stop-card custom ${useCustomPickup ? 'selected' : ''}`}
                                onClick={() => {
                                  setUseCustomPickup(true);
                                  setJoinPickupLocation(null);
                                }}
                              >
                                <div className="bus-stop-card-body">
                                  <span className="bus-stop-nickname">✏️ Custom Location</span>
                                  <span className="bus-stop-address">Enter your own address</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Custom pickup input (if allowed and selected) */}
                        {useCustomPickup && selectedOffer.allow_custom_pickup && (
                          <div className="form-group">
                            <label className="form-label">✏️ {t('eventDashboard.customPickup') || 'Enter Your Pickup Location'}</label>
                            <Suspense fallback={<input type="text" className="form-input" placeholder="Loading..." disabled />}>
                              <MapLocationPicker
                                value={joinPickupLocation?.address || ''}
                                onChange={(value) => setJoinPickupLocation(prev => ({ ...prev, address: value }))}
                                onLocationSelect={(location) => setJoinPickupLocation(location)}
                                placeholder={t('eventDashboard.enterAddress') || 'Search for your address...'}
                                compact={true}
                              />
                            </Suspense>
                          </div>
                        )}

                        {/* Dropoff Stops - Visual Cards */}
                        {dropoffs.length > 0 && (
                          <div className="bus-stops-selection">
                            <label className="form-label">📍 {t('eventDashboard.selectDropoff') || 'Select Drop-off Stop'}</label>
                            <div className="bus-stop-cards">
                              {dropoffs.map((loc, i) => {
                                // Use index as unique identifier to ensure only one location is selected
                                const isSelected = joinDropoffLocation && 
                                  joinDropoffLocation._dropoffIndex === i;
                                
                                return (
                                  <div 
                                    key={`dropoff-${i}-${loc.location_id || i}-${loc.location_address}`}
                                    className={`bus-stop-card ${isSelected ? 'selected' : ''}`}
                                    onClick={() => {
                                      setJoinDropoffLocation({
                                        address: loc.location_address,
                                        lat: loc.location_lat,
                                        lng: loc.location_lng,
                                        _dropoffIndex: i // Store index for comparison
                                      });
                                    }}
                                  >
                                    <div className="bus-stop-card-header">
                                      <span className="bus-stop-number dropoff">{i + 1}</span>
                                      {loc.stop_time && (
                                        <span className="bus-stop-time">🕐 {loc.stop_time}</span>
                                      )}
                                    </div>
                                    <div className="bus-stop-card-body">
                                      {loc.nickname && (
                                        <span className="bus-stop-nickname">{loc.nickname}</span>
                                      )}
                                      <span className="bus-stop-address">{loc.location_address}</span>
                                    </div>
                                    {isSelected && (
                                      <div className="bus-stop-selected-badge">✓</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Show selected locations summary */}
                        {joinPickupLocation?.address && (
                          <div className="pickup-confirmed" style={{ marginTop: '16px' }}>
                            <p className="title">✅ Your Selection</p>
                            <p className="address">🟢 Pickup: {joinPickupLocation.address}</p>
                            {joinDropoffLocation?.address && (
                              <p className="address">🟠 Drop-off: {joinDropoffLocation.address}</p>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <>
                  {/* Car - Show driver's route and allow picking pickup location */}
              {selectedOffer.locations?.[0]?.location_address && event?.event_location && (
                <div className="route-picker-section">
                      <h4>🗺️ {t('eventDashboard.choosePickupOnRoute') || 'Choose pickup location on route'}</h4>
                  
                  <div className="manual-address-box" style={{ marginBottom: '20px' }}>
                        <p>{t('eventDashboard.enterAddressManually') || 'Enter address manually'}:</p>
                    <Suspense fallback={
                      <input 
                        type="text" 
                        className="form-input" 
                        placeholder="Loading..."
                            disabled
                      />
                    }>
                      <MapLocationPicker
                        value={joinPickupLocation?.address || ''}
                            onChange={(value) => setJoinPickupLocation(prev => ({ ...prev, address: value }))}
                        onLocationSelect={(location) => {
                          setJoinPickupLocation(location);
                        }}
                            placeholder={t('eventDashboard.enterPickupAddress') || 'Enter pickup address...'}
                        compact={true}
                            disabled={false}
                      />
                    </Suspense>
                  </div>

                      <p style={{ marginBottom: '12px', fontSize: '14px', color: '#666' }}>{t('eventDashboard.orSelectOnMap') || 'Or select on map'}:</p>
                  <Suspense fallback={<LoadingPlaceholder />}>
                    <RoutePicker
                      origin={selectedOffer.locations[0].location_address}
                      destination={event.event_location}
                      selectedPickup={joinPickupLocation}
                      onPickupSelect={(location) => {
                        setJoinPickupLocation(location);
                      }}
                      height="250px"
                      showInstructions={true}
                    />
                  </Suspense>
                </div>
              )}

              {/* Show selected pickup location confirmation */}
              {joinPickupLocation && joinPickupLocation.lat != null && joinPickupLocation.lng != null ? (
                <div className="pickup-confirmed">
                      <p className="title">✅ {t('eventDashboard.pickupSelected') || 'Pickup location selected'}:</p>
                  <p className="address">{joinPickupLocation.address}</p>
                  <p className="coords">({joinPickupLocation.lat.toFixed(5)}, {joinPickupLocation.lng.toFixed(5)})</p>
                </div>
              ) : (
                <div className="pickup-warning">
                      <p>⚠️ {t('eventDashboard.noPickupSelected') || 'No pickup location selected. Click on the route or enter an address.'}</p>
                </div>
                  )}
                </>
              )}
              
              {isAuthenticated ? (
                <div className="logged-in-notice">
                  <span className="icon">✅</span>
                  <span>Logged in - Your info is pre-filled below</span>
                </div>
              ) : (
                <p className="text-center text-muted mb-2">
                  Enter your details to request a spot
                </p>
              )}

              <div className="form-group">
                <label className="form-label">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Full name"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1234567890"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="email@example.com"
                />
              </div>

              {/* Number of Passengers */}
              <div className="form-group passenger-count-section">
                <label className="form-label">Number of Passengers (Including You) *</label>
                <select
                  name="passengerCount"
                  className="form-select"
                  value={formData.passengerCount || 1}
                  onChange={handleInputChange}
                >
                  {[...Array(Math.min(selectedOffer?.available_seats || 1, 10))].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} {i === 0 ? 'passenger (just you)' : 'passengers'}
                    </option>
                  ))}
                </select>
                <p className="passenger-count-hint">
                  💡 Available seats: {selectedOffer?.available_seats || 0}
                </p>
              </div>

              {/* Message/Note field */}
              <div className="form-group message-to-driver">
                <label className="form-label">Message To Driver (Optional)</label>
                <textarea
                  className="form-input"
                  value={joinMessage}
                  onChange={(e) => setJoinMessage(e.target.value)}
                  placeholder="הערות מיוחדות, זמני גמישות, או כל מידע חשוב לנהג..."
                  rows={3}
                />
              </div>

              {/* Gender Selection */}
              <div className="form-group gender-selector">
                <label className="form-label">Your Gender *</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={formData.gender === 'male'}
                      onChange={handleInputChange}
                    />
                    <span>♂️ Male</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={formData.gender === 'female'}
                      onChange={handleInputChange}
                    />
                    <span>♀️ Female</span>
                  </label>
                </div>
              </div>

              {/* Anonymous Option */}
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isAnonymous"
                  checked={formData.isAnonymous}
                  onChange={handleInputChange}
                />
                <span>🔒 Stay Anonymous (Hide my name and phone from other users)</span>
              </label>

              {/* Payment Options - Only for offers */}
              {(showModal === 'offer' || showModal === 'edit-offer') && (
                <>
                  <div className="form-group">
                    <label className="form-label">Payment</label>
                    <select 
                      name="paymentRequired" 
                      className="form-select" 
                      value={formData.paymentRequired} 
                      onChange={handleInputChange}
                    >
                      <option value="not_required">Not Required (Free Ride)</option>
                      <option value="optional">Optional (Passenger can choose to pay)</option>
                      <option value="obligatory">Obligatory (Payment required)</option>
                    </select>
                  </div>

                  {(formData.paymentRequired === 'optional' || formData.paymentRequired === 'obligatory') && (
                    <div className="form-group">
                      <label className="form-label">Price per Passenger</label>
                      <input
                        type="number"
                        name="paymentAmount"
                        className="form-input"
                        value={formData.paymentAmount}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                      <small className="form-hint">
                        Maximum allowed: 0.5 × distance (km)
                      </small>
                    </div>
                  )}
                </>
              )}

              <label className="checkbox-label remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember my details</span>
              </label>
            </div>

            <div className="legendary-modal-footer">
              <button
                className="legendary-action-btn secondary"
                onClick={() => { setShowModal(null); setJoinPickupLocation(null); setJoinMessage(''); }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="legendary-action-btn success"
                onClick={handleSubmitJoinRequest}
                disabled={submitting}
              >
                {submitting ? '🚗 Sending...' : '🚀 Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal === 'invite-request' && inviteRequest && (
        <div className="legendary-modal-overlay" onClick={() => { setShowModal(null); setInviteRequest(null); setInviteOfferId(''); }}>
          <div className="legendary-modal" onClick={e => e.stopPropagation()}>
            <div className="legendary-modal-handle" />
            <div className="legendary-modal-header">
              <h2>🚗 {t('eventDashboard.offerRide') || 'Offer Ride'}</h2>
              <button className="legendary-modal-close" onClick={() => { setShowModal(null); setInviteRequest(null); setInviteOfferId(''); }}>×</button>
            </div>

            <div className="legendary-modal-body">
              <div className="info-banner info" style={{ alignItems: 'flex-start' }}>
                <span className="info-banner-icon">🙋</span>
                <div>
                  <p style={{ margin: '0 0 8px' }}>
                    {inviteRequest.name || 'Passenger'} • {inviteRequest.passenger_count || 1} passenger(s)
                  </p>
                  {inviteRequest.trip_type && (
                    <p style={{ margin: '0 0 8px' }}>
                      {inviteRequest.trip_type === 'both' ? '🔄 Round trip' : inviteRequest.trip_type === 'going' ? '→ Going' : '← Return'}
                    </p>
                  )}
                  {inviteRequest.arrival_time && (
                    <p style={{ margin: '0 0 8px' }}>
                      🕐 {t('eventDashboard.arriving') || 'Arriving'}: {inviteRequest.arrival_time}
                    </p>
                  )}
                  {inviteRequest.locations?.length > 0 && (
                    <p style={{ margin: 0 }}>
                      📍 {inviteRequest.locations[0].location_address}
                    </p>
                  )}
                </div>
              </div>

              {myOffers.length === 0 ? (
                <div className="legendary-empty-state" style={{ padding: '24px 0' }}>
                  <span className="legendary-empty-icon">🚗</span>
                  <h3>No active offers yet</h3>
                  <p>Create an offer first to invite this passenger.</p>
                  <button
                    className="legendary-btn legendary-btn-primary"
                    onClick={() => navigate(isDemonstrations ? `/dems/event/${eventCode}/publish?mode=offer` : `/event/${eventCode}/publish?mode=offer`)}
                  >
                    Offer a Ride
                  </button>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Select your offer</label>
                  <select
                    className="form-input"
                    value={inviteOfferId}
                    onChange={(e) => setInviteOfferId(e.target.value)}
                  >
                    <option value="">Select an offer</option>
                    {myOffers.map((offer) => {
                      const seats = getSeatsDisplay(offer);
                      return (
                        <option key={offer.offer_id} value={offer.offer_id}>
                          {offer.locations?.[0]?.location_address || 'Location'} • {seats.available}/{seats.total} seats
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
            </div>

            <div className="legendary-modal-footer">
              <button className="legendary-action-btn secondary" onClick={() => { setShowModal(null); setInviteRequest(null); setInviteOfferId(''); }}>
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                className="legendary-action-btn primary"
                onClick={handleSendInvitation}
                disabled={inviteSending || myOffers.length === 0}
              >
                {inviteSending ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join Requests Modal - Legendary */}
      {showModal === 'join-requests' && selectedOffer && (
        <div className="legendary-modal-overlay" onClick={() => setShowModal(null)}>
          <div className="legendary-modal" onClick={e => e.stopPropagation()}>
            <div className="legendary-modal-handle" />
            <div className="legendary-modal-header">
              <h2>📋 Join Requests</h2>
              <button className="legendary-modal-close" onClick={() => setShowModal(null)}>×</button>
            </div>

            <div className="legendary-modal-body">
              {joinRequests.length === 0 ? (
                <div className="legendary-empty-state" style={{ padding: '40px 20px' }}>
                  <span className="legendary-empty-icon">📭</span>
                  <h3>No requests yet</h3>
                  <p>When someone asks to join your ride, they'll appear here.</p>
                </div>
              ) : (
                <div className="join-requests-list">
                  {joinRequests.map((req, i) => (
                    <div key={i} className={`join-request-item ${req.status}`}>
                      <div className="request-info">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <h4 style={{ margin: 0 }}>👤 {req.name || 'Unknown'}</h4>
                          {(() => {
                            const passengerCount = parseInt(req.passenger_count) || 1;
                            return (
                              <span style={{ fontSize: '16px', fontWeight: 700, color: passengerCount > 1 ? '#059669' : '#64748b', padding: '6px 14px', background: passengerCount > 1 ? '#d1fae5' : '#f1f5f9', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                {passengerCount > 1 ? '👥' : '👤'} {passengerCount} {passengerCount === 1 ? 'passenger' : 'passengers'}
                              </span>
                            );
                          })()}
                          <span style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>
                            {req.gender === 'male' ? '♂️ Male' : req.gender === 'female' ? '♀️ Female' : '—'}
                          </span>
                        </div>
                        <p style={{ margin: '4px 0', fontSize: '14px' }}>
                          📱 {req.phone || 'No phone'} {req.email && '•'} {req.email && `📧 ${req.email}`}
                        </p>
                        {req.pickup_location && (
                          <p className="request-pickup-location" style={{ margin: '8px 0', fontSize: '14px' }}>
                            <span>📍</span>
                            <span>Pickup Location: {req.pickup_location}</span>
                          </p>
                        )}
                        {req.pickup_lat != null && req.pickup_lng != null && (
                          <p style={{ margin: '4px 0', fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>
                            Coordinates: ({typeof req.pickup_lat === 'number' ? req.pickup_lat.toFixed(6) : parseFloat(req.pickup_lat).toFixed(6)}, {typeof req.pickup_lng === 'number' ? req.pickup_lng.toFixed(6) : parseFloat(req.pickup_lng).toFixed(6)})
                          </p>
                        )}
                        {req.message && (
                          <div className="passenger-message-box" style={{ marginTop: '12px', padding: '12px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                            <p className="label" style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 600, color: '#0c4a6e' }}>💬 Message from passenger:</p>
                            <p className="message" style={{ margin: 0, fontSize: '14px', color: '#075985' }}>{req.message}</p>
                          </div>
                        )}
                        {req.created_at && (
                          <p style={{ margin: '8px 0 4px', fontSize: '12px', color: '#94a3b8' }}>
                            📅 Requested: {new Date(req.created_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                        <span className={`status-badge status-${req.status}`} style={{ marginTop: '8px', display: 'inline-block' }}>
                          {req.status === 'pending' ? '⏳ Pending' : req.status === 'confirmed' ? '✅ Confirmed' : '❌ Declined'}
                        </span>
                      </div>
                      
                      {/* Show route with detour if pickup location is available */}
                      {(() => {
                        const hasPickupLocation = req.pickup_location || (req.pickup_lat != null && req.pickup_lng != null);
                        const hasPickupCoords = req.pickup_lat != null && req.pickup_lng != null;
                        const originLoc = selectedOffer.locations?.[0];
                        const hasOrigin = originLoc?.location_address;
                        const hasOriginCoords = originLoc?.location_lat != null && originLoc?.location_lng != null;
                        const hasDestination = event?.event_location;
                        const hasDestCoords = event?.location_lat != null && event?.location_lng != null;

                        if (hasPickupCoords && hasOrigin && hasDestination) {
                          return (
                            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                              <Suspense fallback={<div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', borderRadius: '12px' }}>Loading map...</div>}>
                                <RouteWithDetour
                                  origin={hasOriginCoords ? {
                                    address: originLoc.location_address,
                                    lat: parseFloat(originLoc.location_lat),
                                    lng: parseFloat(originLoc.location_lng)
                                  } : originLoc.location_address}
                                  destination={hasDestCoords ? {
                                    address: event.event_location,
                                    lat: parseFloat(event.location_lat),
                                    lng: parseFloat(event.location_lng)
                                  } : event.event_location}
                                  pickupLocation={{
                                    address: req.pickup_location || 'Pickup location',
                                    lat: typeof req.pickup_lat === 'number' ? req.pickup_lat : parseFloat(req.pickup_lat),
                                    lng: typeof req.pickup_lng === 'number' ? req.pickup_lng : parseFloat(req.pickup_lng)
                                  }}
                                  passengerName={req.name || req.passenger_name}
                                  height="300px"
                                />
                              </Suspense>
                            </div>
                          );
                        } else if (hasPickupLocation && !hasPickupCoords) {
                          return (
                            <div style={{ marginTop: '12px', padding: '12px', background: '#fef3c7', borderRadius: '8px' }}>
                              <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                                📍 Pickup: {req.pickup_location}
                                <span style={{ fontSize: '11px', opacity: 0.8 }}> (coordinates not available for route calculation)</span>
                              </p>
                            </div>
                          );
                        }
                        return (
                          <div style={{ marginTop: '12px', padding: '12px', background: '#f1f5f9', borderRadius: '8px' }}>
                            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                              📍 No pickup location specified
                            </p>
                          </div>
                        );
                      })()}
                      
                      {req.status === 'pending' && (
                        <div className="request-actions">
                          <button onClick={() => handleAcceptJoinRequest(req.id)} className="btn btn-success btn-sm">
                            ✅ Accept
                          </button>
                          <button onClick={() => handleRejectJoinRequest(req.id)} className="btn btn-ghost btn-sm">
                            ❌
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="legendary-modal-footer">
              <button className="legendary-action-btn secondary" onClick={() => setShowModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Passengers Modal - Legendary */}
      {showPassengersModal && (
        <div className="legendary-modal-overlay" onClick={() => { setShowPassengersModal(null); setCancelMessage(''); }}>
          <div className="legendary-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <div className="legendary-modal-handle" />
            <div className="legendary-modal-header">
              <h2>👥 Manage Passengers</h2>
              <button className="legendary-modal-close" onClick={() => { setShowPassengersModal(null); setCancelMessage(''); }}>×</button>
            </div>

            <div className="legendary-modal-body">
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px', direction: 'rtl' }}>
                כאן תוכל לנהל את הנוסעים המאושרים לנסיעה שלך. אם תרצה לבטל נוסע, תוכל לשלוח לו הודעה עם הסיבה.
              </p>
              
              {showPassengersModal.confirmed_passengers?.length > 0 ? (
                <div className="passengers-list">
                  {showPassengersModal.confirmed_passengers.map((passenger, i) => (
                    <div key={i} className="passenger-manage-item" style={{
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '10px',
                      marginBottom: '12px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>👤 {passenger.name}</h4>
                          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>{passenger.phone}</p>
                          {passenger.email && (
                            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>{passenger.email}</p>
                          )}
                          {passenger.pickup_location && (
                            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#0369a1' }}>
                              📍 {passenger.pickup_location}
                            </p>
                          )}
                          {passenger.message && (
                            <div style={{ marginTop: '8px', padding: '8px', background: '#f0f9ff', borderRadius: '6px' }}>
                              <p style={{ margin: 0, fontSize: '12px', color: '#0c4a6e', direction: 'rtl' }}>
                                💬 {passenger.message}
                              </p>
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <a 
                            href={`https://wa.me/${formatPhone(passenger.phone)}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-success btn-sm"
                            style={{ textDecoration: 'none' }}
                          >
                            💬 WhatsApp
                          </a>
                          <a 
                            href={`tel:${passenger.phone}`} 
                            className="btn btn-secondary btn-sm"
                            style={{ textDecoration: 'none' }}
                          >
                            📞
                          </a>
                        </div>
                      </div>
                      
                      {/* Cancel section */}
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                        <details style={{ cursor: 'pointer' }}>
                          <summary style={{ fontSize: '13px', color: '#dc2626', fontWeight: 500 }}>
                            ❌ Cancel this passenger
                          </summary>
                          <div style={{ marginTop: '12px' }}>
                            <textarea
                              placeholder="הודעה לנוסע (למה הביטול)..."
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                border: '1px solid #fca5a5',
                                fontSize: '13px',
                                direction: 'rtl',
                                resize: 'vertical',
                                minHeight: '60px'
                              }}
                              value={cancelMessage}
                              onChange={(e) => setCancelMessage(e.target.value)}
                            />
                            <button 
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to cancel ${passenger.name}?`)) {
                                  try {
                                    const response = await fetch(`${API_URL}/carpool/offer/${showPassengersModal.offer_id}/cancel-passenger`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ 
                                        passenger_id: passenger.id,
                                        message: cancelMessage 
                                      }),
                                    });
                                    if (response.ok) {
                                      showToast('Passenger cancelled successfully', 'success');
                                      setShowPassengersModal(null);
                                      setCancelMessage('');
                                      await fetchOffers();
                                    } else {
                                      const data = await response.json();
                                      showToast(data.message || 'Failed to cancel passenger', 'error');
                                    }
                                  } catch (error) {
                                    showToast('Error cancelling passenger', 'error');
                                  }
                                }
                              }}
                              className="btn btn-danger btn-sm"
                              style={{ marginTop: '8px' }}
                            >
                              Confirm Cancel
                            </button>
                          </div>
                        </details>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="legendary-empty-state" style={{ padding: '40px 20px' }}>
                  <span className="legendary-empty-icon">👥</span>
                  <h3>No confirmed passengers yet</h3>
                  <p>When you accept passengers, they'll appear here.</p>
                </div>
              )}
            </div>

            <div className="legendary-modal-footer">
              <button className="legendary-action-btn secondary" onClick={() => { setShowPassengersModal(null); setCancelMessage(''); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Picker Modal - for offers with multiple organizers */}
      {contactPickerOffer && (
        <div className="legendary-modal-overlay" onClick={() => { setContactPickerOffer(null); setContactPickerAction(null); }}>
          <div className="legendary-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="legendary-modal-header">
              <h2>
                {contactPickerAction === 'whatsapp' 
                  ? (t('eventDashboard.selectWhatsAppContact') || '📱 اختر من تريد التواصل معه')
                  : (t('eventDashboard.selectPhoneContact') || '📞 اختر من تريد الاتصال به')}
              </h2>
              <button className="legendary-modal-close" onClick={() => { setContactPickerOffer(null); setContactPickerAction(null); }}>×</button>
            </div>
            <div className="legendary-modal-body" style={{ padding: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {getOfferContacts(contactPickerOffer).map((contact, index) => (
                  <button
                    key={index}
                    onClick={() => handleContactSelect(contact)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '16px',
                      background: contact.isMain ? 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)' : 'rgba(99, 102, 241, 0.1)',
                      color: contact.isMain ? 'white' : 'inherit',
                      border: contact.isMain ? 'none' : '1px solid rgba(99, 102, 241, 0.3)',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      textAlign: 'right',
                      transition: 'all 0.2s ease',
                      width: '100%'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      background: contact.isMain ? 'rgba(255,255,255,0.2)' : 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      color: 'white',
                      flexShrink: 0
                    }}>
                      {contact.isMain ? '🚗' : '👤'}
                    </div>
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
                        {contact.name}
                        {contact.isMain && (
                          <span style={{ 
                            fontSize: '11px', 
                            background: 'rgba(255,255,255,0.3)', 
                            padding: '2px 8px', 
                            borderRadius: '10px',
                            marginRight: '8px'
                          }}>
                            {t('eventDashboard.mainDriver') || 'السائق الرئيسي'}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', opacity: 0.8, direction: 'ltr' }}>
                        {contact.phone}
                      </div>
                    </div>
                    <div style={{ fontSize: '24px' }}>
                      {contactPickerAction === 'whatsapp' ? '💬' : '📞'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventDashboard;
